// apps/web/src/components/StartedTrainingCard.tsx
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Pause, Play, RotateCcw, X, ChevronRight, Check, RefreshCcw, Volume2, VolumeX, Users, Package } from 'lucide-react';
import { useStopwatch, fmtTime } from '../hooks/useStopwatch';
import { useWakeLock } from '../hooks/useWakeLock';
import {
  unlockWorkoutAudio,
  isWorkoutSoundMuted,
  setWorkoutSoundMuted,
  cueTick,
  cueGo,
  cueEnd,
} from '../lib/workoutSound';
import {
  useStartedTraining,
  PlannedTrainingDTO,
  StartedTrainingResumeState,
  StartedTrainingSetRecord,
  StartedTrainingExerciseProgress,
} from '../context/StartedTrainingContext';
import { Card, CardHeader, CardBody } from './ui/Card';
import { Button } from './ui/Button';
import { showToast } from './ui/Toast';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../auth/AuthProvider';
import { useProfile } from '../context/ProfileContext';
import IgygHostPanel, { type PanelExercise } from './IgygHostPanel';
import { isHoldExerciseLabel } from '../methods/bodybuildingFitness';
import { ErrorBoundary } from './ui/ErrorBoundary';
import { API_BASE } from '../lib/apiBase';
import { serverNow, syncServerClock } from '../lib/clockSync';
import { subscribeGlobalStart } from '../lib/realtime';
import { authFetch, authFetchJSON } from '../lib/authFetch';
import { getWodRxForExercise, getWodRxNote } from '../lib/wodRx';
import { prettyEquipment } from '../lib/equipment';
import {
  formatBarbellStackLabel,
  hasBarbellEquipment,
  isLikelyBarbellExerciseName,
  KG_PLATE_STYLE,
  LB_PLATE_STYLE,
  resolveBarbellStackView,
  type PlateStrategy,
} from '../lib/barbell';
import {
  clearStartedTrainingSnapshot,
  clearStartedTrainingSnapshotRemote,
  saveStartedTrainingSnapshotRemote,
  writeStartedTrainingSnapshot,
} from '../lib/startedTrainingSnapshot';

type Props = { training: PlannedTrainingDTO };

// Aliases, not copies. These were a second, independent declaration of the same
// shape, which is a problem because `progress` is round-tripped through the
// resume snapshot as the context's type — so the two had to agree and nothing
// made them. Adding `pauseMs` is what surfaced it: the context knew about the
// field and this file did not.
type SetRecord = StartedTrainingSetRecord;
type ExerciseProgress = StartedTrainingExerciseProgress;

type Step = {
  globalIdx: number;
  setIndex: number;
};

type AmrapRoundTimes = { blockId: string; roundTimesMs: number[] };
type JoinStateDTO = {
  id?: string;
  globalStartedAt?: string | null;
  globalStartedBy?: string | null;
  bookedByMe?: boolean;
  readyByMe?: boolean;
  isOwner?: boolean;
};
const GLOBAL_START_COUNTDOWN_SEC = 5;
const GLOBAL_START_COUNTDOWN_MS = GLOBAL_START_COUNTDOWN_SEC * 1000;

function normalizeRounds(value: unknown): number | null {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const intVal = Math.floor(num);
  return intVal > 0 ? intVal : null;
}

function normalizeTimeCapSec(value: unknown): number | null {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const intVal = Math.floor(num);
  return intVal > 0 ? intVal : null;
}

function getTimeCapSec(methodMeta: any): number | null {
  const sec = normalizeTimeCapSec(methodMeta?.timeCapSec);
  if (sec) return sec;
  const legacyMin = normalizeTimeCapSec(methodMeta?.timeCapMin);
  return legacyMin ? legacyMin * 60 : null;
}
function isDistanceExerciseLabel(label?: string | null): boolean {
  const value = String(label ?? '').trim().toLowerCase();
  if (!value) return false;
  if (/\bbear crawl\b/.test(value)) return true;
  if (/\bshuttle sprints?\b/.test(value)) return true;
  return /\bmeter(s)?\b/.test(value);
}

function isShuttleSprintExerciseLabel(label?: string | null): boolean {
  const value = String(label ?? '').trim().toLowerCase();
  if (!value) return false;
  return /\bshuttle sprints?\b/.test(value);
}

function hasPositiveNumber(values: unknown): boolean {
  if (!Array.isArray(values)) return false;
  return values.some((v) => typeof v === 'number' && v > 0);
}

function getRepUnit(
  label?: string | null,
  options?: { shuttleUsesRepsDistance?: boolean }
): 'm' | 'reps' | 'sec' {
  // A hold is timed, not counted — a plank shows "30 sec", never "30 reps".
  // Checked first: a hold is never a distance or shuttle movement, and the
  // Planner already stores its numbers as seconds (see isHoldExerciseLabel).
  if (isHoldExerciseLabel(label)) return 'sec';
  const shuttle = isShuttleSprintExerciseLabel(label);
  const shuttleUsesRepsDistance = !!options?.shuttleUsesRepsDistance;
  return isDistanceExerciseLabel(label) && (!shuttle || !shuttleUsesRepsDistance)
    ? 'm'
    : 'reps';
}

function PlateIcon({ weight, unit }: { weight: number; unit: 'kg' | 'lb' }) {
  const styles = unit === 'lb' ? LB_PLATE_STYLE : KG_PLATE_STYLE;
  const fallback = unit === 'lb' ? LB_PLATE_STYLE[45] : KG_PLATE_STYLE[25];
  const style = styles[weight] ?? fallback;
  return (
    <span
      className={`inline-block border ${style.className} rounded-[3px]`}
      style={{ width: style.width, height: style.height }}
      title={`${weight} ${unit}`}
      aria-hidden="true"
    />
  );
}

function BarbellStack({
  totalKg,
  strategy,
  rxLoadLabel,
}: {
  totalKg: number;
  strategy: PlateStrategy;
  rxLoadLabel?: string | null;
}) {
  const stack = resolveBarbellStackView({ totalKg, strategy, rxLoadLabel });
  if (!stack) return null;

  const sorted = stack.plates.slice().sort((a, b) => b - a);
  const left = sorted.slice().reverse();

  return (
    <span
      className="inline-flex items-center gap-[2px] rounded-full px-1.5 py-[2px] bg-slate-100/80 dark:bg-slate-800/70 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
      title={formatBarbellStackLabel(stack)}
      aria-hidden="true"
    >
      <span className="inline-flex items-center gap-[2px]">
        {left.map((plate, index) => (
          <PlateIcon key={`l-${stack.unit}-${plate}-${index}`} weight={plate} unit={stack.unit} />
        ))}
      </span>
      <span className="inline-block h-[2px] w-5 bg-slate-400 dark:bg-slate-500 rounded-full" />
      <span className="inline-flex items-center gap-[2px]">
        {sorted.map((plate, index) => (
          <PlateIcon key={`r-${stack.unit}-${plate}-${index}`} weight={plate} unit={stack.unit} />
        ))}
      </span>
    </span>
  );
}

function getDropsetExerciseIds(plannerMeta?: { blocks?: any[] } | null): Set<string> {
  const ids = new Set<string>();
  const blocks = Array.isArray(plannerMeta?.blocks) ? plannerMeta!.blocks : [];
  for (const block of blocks) {
    const methodMeta = block?.methodMeta;
    const dropsetIds = Array.isArray(methodMeta?.dropsetExerciseIds)
      ? methodMeta.dropsetExerciseIds
      : [];
    for (const id of dropsetIds) {
      if (typeof id === 'string' && id.trim()) ids.add(id);
    }
  }
  return ids;
}

function initRepOverridesForExercise(ex: any): Array<number | null> {
  const repsRaw = Array.isArray(ex?.repsPerSet) ? ex.repsPerSet : [];
  const setsRaw = Number(ex?.targetSets ?? (repsRaw.length || 1));
  const sets = Number.isFinite(setsRaw) && setsRaw > 0 ? Math.floor(setsRaw) : 1;
  const reps: Array<number | null> = [];

  for (let i = 0; i < sets; i += 1) {
    const v = repsRaw[i];
    reps.push(Number.isFinite(v) ? Math.floor(v) : null);
  }

  return reps;
}

// Resolve the planned rep for a given round/set index.
// Round-based methods (EMOM, X Rounds) repeat the same exercise across more
// rounds than it has explicit rep entries — e.g. Chelsea's 5 pull-ups is stored
// once but shown every round. When setIndex runs past the defined reps, repeat
// the last defined value instead of blanking every round after the first.
// In-bounds values are returned as-is (a deliberate null, e.g. a dropset's final
// set, still yields null so it can render as "X").
function resolveRepForSet(
  repOverridesRow: Array<number | null> | undefined,
  repsPerSet: unknown,
  setIndex: number
): number | null {
  const override = repOverridesRow?.[setIndex];
  if (typeof override === 'number' && Number.isFinite(override)) return Math.floor(override);
  const arr = Array.isArray(repsPerSet) ? repsPerSet : [];
  if (setIndex < arr.length) {
    return Number.isFinite(arr[setIndex]) ? Math.floor(Number(arr[setIndex])) : null;
  }
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    if (Number.isFinite(arr[i])) return Math.floor(Number(arr[i]));
  }
  return null;
}

function getGirlWodTiming(rawId?: string | null): { mode: 'amrap' | 'emom'; durationMin: number; intervalSec?: number } | null {
  const girlId = String(rawId ?? '').trim().toLowerCase();
  if (!girlId) return null;
  if (girlId === 'cindy') return { mode: 'amrap', durationMin: 20 };
  if (girlId === 'mary') return { mode: 'amrap', durationMin: 20 };
  if (girlId === 'chelsea') return { mode: 'emom', durationMin: 30, intervalSec: 60 };
  return null;
}

function getBlockTiming(block: any | null): { mode: 'amrap' | 'emom' | null; durationMin: number | null; intervalSec: number | null; rounds: number | null } {
  const girlTiming = getGirlWodTiming(block?.methodMeta?.girlWod);
  const modeRaw = block?.methodMeta?.mode;
  const mode =
    modeRaw === 'emom'
      ? 'emom'
      : modeRaw === 'amrap'
        ? 'amrap'
        : girlTiming?.mode ?? null;
  const durationRaw = Number(block?.methodMeta?.durationMin ?? girlTiming?.durationMin);
  const durationMin =
    Number.isFinite(durationRaw) && durationRaw > 0 ? Math.floor(durationRaw) : null;
  const intervalRaw = Number(block?.methodMeta?.intervalSec ?? girlTiming?.intervalSec);
  const intervalSec =
    Number.isFinite(intervalRaw) && intervalRaw > 0 ? Math.floor(intervalRaw) : null;
  const roundsRaw = Number(block?.methodMeta?.rounds);
  const rounds = Number.isFinite(roundsRaw) && roundsRaw > 0 ? Math.floor(roundsRaw) : null;
  return { mode, durationMin, intervalSec, rounds };
}

/**
 * How long an EMOM block actually runs, as text.
 *
 * rounds × interval is exact; durationMin only holds whole minutes, so an
 * interval that isn't a minute makes it lie — 7 × 1:30 is 10:30 but stores as
 * 11. Whole minutes are still said the familiar way ("10 min") because that is
 * how an EMOM is normally named; anything else is spelled out as m:ss rather
 * than rounded to a number that contradicts the work. Blocks saved before the
 * Rounds field existed have nothing exact to offer, so they keep the minutes.
 */
function formatEmomTotal(
  rounds: number | null,
  intervalSec: number | null,
  durationMin: number | null
): string | null {
  if (rounds && intervalSec) {
    const totalSec = rounds * intervalSec;
    return totalSec % 60 === 0 ? `${totalSec / 60} min` : fmtTime(totalSec * 1000);
  }
  return durationMin ? `${durationMin} min` : null;
}

/**
 * One planned value per set, collapsed to the shortest text that still says it.
 *
 * "10/10/10" is three ways of saying 10 and the preview has one line per
 * exercise to spend, but "10/8/6" has to survive intact — the shape of the
 * pyramid is the instruction. `''` means "not planned": a run of them drops out
 * (most sets carry no weight), and all of them means there is nothing to show.
 */
function collapsePerSet(values: string[]): string {
  const filled = values.filter(Boolean);
  if (!filled.length) return '';
  if (filled.length !== values.length) return filled.join('/');
  return values.every((v) => v === values[0]) ? values[0] : values.join('/');
}

// Tags that name no object to go and fetch, so they would only pad the list.
const EQUIPMENT_NOISE = new Set(['none', 'other', 'gym']);

/**
 * The kit a block needs, deduped across its movements, in first-use order.
 *
 * `bodyweight` drops out as soon as anything else is needed — a block that is
 * half pull-ups does not send you looking for your bodyweight — but stays when
 * it is all there is, so an all-bodyweight block says so rather than showing an
 * empty list. A movement tagged dumbbell AND kettlebell means *either* will do
 * (see `canPerformWith`), so it must never list both: it contributes one joined
 * entry, and nothing at all when a neighbouring movement already pins one of
 * the two. That pair is emitted as `dumbbell|kettlebell` for the renderer to
 * split — every other entry is a real catalog tag.
 */
function collectBlockEquipment(exercises: any[]): string[] {
  const definite: string[] = [];
  const eitherRows: string[][] = [];
  for (const ex of exercises) {
    const tags: string[] = (Array.isArray(ex?.equipment) ? ex.equipment : [])
      .map((e: any) => String(e ?? '').trim().toLowerCase())
      .filter((e: string) => e && !EQUIPMENT_NOISE.has(e));
    if (tags.includes('dumbbell') && tags.includes('kettlebell')) eitherRows.push(tags);
    else definite.push(...tags);
  }

  const out: string[] = [];
  const seen = new Set<string>();
  const push = (tag: string) => {
    if (seen.has(tag)) return;
    seen.add(tag);
    out.push(tag);
  };
  definite.forEach(push);
  // Second pass on purpose: whether an "either" row needs its own entry depends
  // on what the definite rows already ask for, so it can only be decided once
  // they are all in.
  for (const tags of eitherRows) {
    tags.filter((tag) => tag !== 'dumbbell' && tag !== 'kettlebell').forEach(push);
    if (!seen.has('dumbbell') && !seen.has('kettlebell')) push('dumbbell|kettlebell');
  }

  const loaded = out.filter((tag) => tag !== 'bodyweight');
  return loaded.length ? loaded : out;
}

function sumMs(values: number[]): number {
  return values.reduce((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0);
}

function normalizeIsoOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function isoToMs(value: unknown): number | null {
  const iso = normalizeIsoOrNull(value);
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

export default function StartedTrainingCard({ training }: Props) {
  const { t } = useLocale();
  const { session } = useAuth();
  const { profile } = useProfile();
  const { stopTraining } = useStartedTraining();
  useWakeLock(true); // keep the screen awake for the duration of the workout

  const plannedId = training.plannedId ?? training.id ?? null;
  const requiresGlobalStart = !!(training.requiresGlobalStart || training.coachFriendId);
  const [globalStartedAt, setGlobalStartedAt] = React.useState<string | null>(
    normalizeIsoOrNull(training.globalStartedAt)
  );
  const [readyByMe, setReadyByMe] = React.useState<boolean>(!!training.readyByMe);
  const [globalSyncLoading, setGlobalSyncLoading] = React.useState(false);
  const [globalSyncError, setGlobalSyncError] = React.useState<string | null>(null);
  const [readySaving, setReadySaving] = React.useState(false);

  // ---------- High-res timers (ms) ----------
  const [workoutMs, setWorkoutMs] = React.useState(0);
  const workoutRef = React.useRef<number | null>(null);
  const workoutBaseRef = React.useRef(0);
  const workoutStartRef = React.useRef<number | null>(null);
  const workoutStartedAtRef = React.useRef<number | null>(null);
  const workoutMsRef = React.useRef(0);

  const [exerciseMs, setExerciseMs] = React.useState(0);
  const exerciseRef = React.useRef<number | null>(null);
  const exerciseBaseRef = React.useRef(0);
  const exerciseStartRef = React.useRef<number | null>(null);

  // Pause stopwatch (simple hook) — the pause of the *current set*, zeroed at
  // every set boundary so each set can record its own.
  const pauseSw = useStopwatch({ autoStart: false });
  // …and a second one for the whole workout, which is why these have to be two
  // stopwatches rather than one plus arithmetic: the finish payload used to send
  // `pauseSw.elapsed` as `totalPauseMs` while that same stopwatch was being reset
  // on every exercise change, so the stored "total" was only ever the last
  // exercise's pause. Nothing displayed it, so it was silently wrong for as long
  // as it has existed. Running the total on its own stopwatch makes it correct by
  // construction instead of by remembering to bank at every boundary — the only
  // rule is that this one starts and stops in lockstep with `pauseSw` and is
  // reset solely when a workout genuinely begins again.
  const pauseTotalSw = useStopwatch({ autoStart: false });

  // ---------- UI state ----------
  const [paused, setPaused] = React.useState(false);
  const [soundMuted, setSoundMuted] = React.useState(isWorkoutSoundMuted());
  const toggleSound = React.useCallback(() => {
    setSoundMuted((m) => {
      const next = !m;
      setWorkoutSoundMuted(next);
      return next;
    });
  }, []);
  // Unlock audio on the first interaction inside the workout (fallback if the
  // launching tap didn't already; browsers keep the AudioContext suspended otherwise).
  React.useEffect(() => {
    const unlock = () => unlockWorkoutAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);
  const [setDoneConfirmed, setSetDoneConfirmed] = React.useState(false);
  const [countdown, setCountdown] = React.useState<number>(5);
  const [countdownKind, setCountdownKind] = React.useState<'start' | 'rest' | 'method' | null>(null);
  const [roundWorkflowDoneAtMs, setRoundWorkflowDoneAtMs] = React.useState<number | null>(null);

  const exList = training.exercises ?? [];
  const resumeState =
    training.resumeMode === 'resume' ? training.resumeState ?? null : null;
  const waitingForCoachStart = requiresGlobalStart && !globalStartedAt;

  const loadJoinState = React.useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!plannedId || !requiresGlobalStart) return;
      const silent = !!opts?.silent;
      if (!silent) setGlobalSyncLoading(true);
      setGlobalSyncError(null);
      try {
        const res = await authFetchJSON<JoinStateDTO>(
          `${API_BASE}/api/trainings/${plannedId}/join-state`
        );
        if (!res.ok) {
          const errText = res.errorText || t('Failed to load workout state', 'Failed to load workout state');
          if (!silent) showToast(errText, 'error');
          setGlobalSyncError(errText);
          return;
        }
        const nextStartedAt = normalizeIsoOrNull(res.data?.globalStartedAt);
        setGlobalStartedAt(nextStartedAt);
        setReadyByMe(!!res.data?.readyByMe);
      } finally {
        if (!silent) setGlobalSyncLoading(false);
      }
    },
    [plannedId, requiresGlobalStart, t]
  );

  const handleMarkReady = React.useCallback(async () => {
    if (!plannedId) return;
    setReadySaving(true);
    try {
      const res = await authFetchJSON(`${API_BASE}/api/trainings/${plannedId}/ready`, {
        method: 'POST',
      });
      if (!res.ok) {
        if (res.errorText === 'not_booked') {
          showToast(t('Please book workout first', 'Please book workout first'), 'error');
        } else {
          showToast(res.errorText || t('Failed to mark ready', 'Failed to mark ready'), 'error');
        }
        return;
      }
      setReadyByMe(true);
      showToast(t('Marked as ready', 'Marked as ready'), 'success');
    } finally {
      setReadySaving(false);
    }
  }, [plannedId, t]);
  const [repOverrides, setRepOverrides] = React.useState<Array<Array<number | null>>>(
    exList.map(initRepOverridesForExercise)
  );
  const dropsetExerciseIds = React.useMemo(
    () => getDropsetExerciseIds(training?.plannerMeta ?? null),
    [training?.plannerMeta]
  );

  const methodBlocks = React.useMemo(() => {
    const blocks = (training as any)?.plannerMeta?.blocks;
    if (Array.isArray(blocks) && blocks.length > 0) return blocks;
    const exerciseIds = exList.map((ex) => ex?.id).filter(Boolean);
    return [
      {
        id: 'method-1',
        methodId: training?.method?.id ?? null,
        exerciseIds,
        methodMeta: null,
      },
    ];
  }, [training, exList]);

  const [blockIdx, setBlockIdx] = React.useState(0);
  const [pendingNextMethodIdx, setPendingNextMethodIdx] = React.useState<number | null>(null);
  const [stepIdx, setStepIdx] = React.useState(0); // index within current block steps

  const currentBlock =
    methodBlocks[Math.min(blockIdx, Math.max(0, methodBlocks.length - 1))] || null;
  const currentBlockId = String(currentBlock?.id ?? `method-${blockIdx + 1}`);
  const currentTiming = getBlockTiming(currentBlock);
  const isPartnerIgyg = (currentBlock as any)?.partnerMode === 'igyg';
  const methodMode = currentTiming.mode;
  const methodDurationMin = currentTiming.durationMin;
  const isEmom = methodMode === 'emom';
  const isAmrap = !isEmom && typeof methodDurationMin === 'number';
  const emomIntervalSec = currentTiming.intervalSec ?? 60;
  const emomIntervalMs = emomIntervalSec * 1000;
  const ladderRounds = Array.isArray((currentBlock as any)?.methodMeta?.ladder)
    ? (currentBlock as any).methodMeta.ladder.filter((v: any) => Number.isFinite(Number(v)) && Number(v) > 0)
    : [];
  const xRoundsCount = normalizeRounds((currentBlock as any)?.methodMeta?.rounds);
  const roundCount = ladderRounds.length ? ladderRounds.length : xRoundsCount;
  const isRoundBased = !!roundCount;
  const roundLabel = isRoundBased ? t('Round', 'Round') : t('Set', 'Set');
  const forTimeEnabled = !!(currentBlock as any)?.methodMeta?.forTime;
  const timeCapSec = forTimeEnabled ? getTimeCapSec((currentBlock as any)?.methodMeta) : null;
  const isXRoundsMethod = xRoundsCount > 0 && ladderRounds.length === 0;
  const useRoundWorkflow = isXRoundsMethod && !isEmom && !isAmrap;
  // Complex: the interval holds the whole block rather than one rotating
  // exercise, so each interval is a round of every movement and the leftover
  // time is the athlete's rest. Shown with the round list, but advanced by the
  // clock instead of a "finish round" tap.
  const isComplexEmom = isEmom && (currentBlock as any)?.methodMeta?.complex === true;
  // Both views list the whole block for one round; they differ in what advances them.
  const useRoundList = useRoundWorkflow || isComplexEmom;

  // The same exercise may appear in several blocks (Row in the warm-up and again
  // in the WOD), and each instance has its own flat row. Resolve every block in
  // one left-to-right pass over the rows — which are ordered by `order`, block by
  // block — so a block claims its OWN rows. Resolving per block would restart at
  // row 0 each time and alias every later block back onto the first occurrence,
  // showing the wrong targets and overwriting the earlier block's progress.
  const blockIndicesByBlock = React.useMemo(() => {
    const used = new Set<number>();
    return methodBlocks.map((block: any) => {
      const ids = Array.isArray(block?.exerciseIds) ? block.exerciseIds : [];
      if (!ids.length) return exList.map((_, i) => i);
      const indices: number[] = [];
      for (const id of ids) {
        let found = exList.findIndex((ex, i) => ex?.id === id && !used.has(i));
        if (found >= 0) {
          used.add(found);
        } else {
          // Fewer rows than the block references (hand-written data): fall back to
          // any row for that exercise rather than dropping it from the workout.
          found = exList.findIndex((ex) => ex?.id === id);
        }
        if (found >= 0) indices.push(found);
      }
      return indices;
    });
  }, [methodBlocks, exList]);

  const blockExerciseIndices = React.useMemo(
    () => blockIndicesByBlock[blockIdx] ?? [],
    [blockIndicesByBlock, blockIdx]
  );

  const blockExerciseDetails = React.useMemo(() => {
    return blockExerciseIndices
      .map((globalIdx) => {
        const ex = exList[globalIdx];
        const id = ex?.id;
        if (!id) return null;
        const setsRaw = Number(ex?.targetSets ?? 1);
        const sets = Number.isFinite(setsRaw) && setsRaw > 0 ? Math.floor(setsRaw) : 1;
        return { globalIdx, id, sets };
      })
      .filter(Boolean) as Array<{ globalIdx: number; id: string; sets: number }>;
  }, [blockExerciseIndices, exList]);

  // ── "I Go You Go" host panel inputs (used when the current block is IGYG) ──
  const igygSelfUserId = session?.user?.id ?? '';
  const igygSelfName = (profile?.username || session?.user?.email || 'Partner') as string;
  const igygExercises = React.useMemo<PanelExercise[]>(() => {
    return blockExerciseDetails.map((d) => {
      const ex: any = exList[d.globalIdx];
      const name = (ex?.nameRaw ?? ex?.raw ?? d.id) as string;
      const rps: any = ex?.repsPerSet;
      let target: number | null = null;
      if (Array.isArray(rps)) target = rps.reduce((a: number, b: any) => a + (Number(b) || 0), 0);
      else if (Number.isFinite(Number(rps))) target = Math.max(0, Math.floor(Number(rps))) * d.sets;
      return { id: d.id, name, target: target || null };
    });
  }, [blockExerciseDetails, exList]);

  // Build per-set steps so supersets alternate (A1, B1, A2, B2, ...)
  const blockSteps = React.useMemo(() => {
    const steps: Step[] = [];
    if (blockExerciseDetails.length === 0) return steps;

    const rounds = roundCount;
    if (rounds) {
      const maxSets = Math.max(rounds, ...blockExerciseDetails.map((ex) => ex.sets));
      for (let s = 0; s < maxSets; s++) {
        for (const ex of blockExerciseDetails) {
          // Every exercise belongs to every round — trainings created outside the
          // Planner may store sets=1, which would otherwise collapse the block to
          // a single round and jump straight to "Method complete".
          if (s < rounds || s < ex.sets) steps.push({ globalIdx: ex.globalIdx, setIndex: s });
        }
      }
      return steps;
    }

    const blockIdSet = new Set(blockExerciseDetails.map((ex) => ex.id));
    const supersetByFirst = new Map<string, string>();
    const rawPairs = (currentBlock as any)?.methodMeta?.supersetPairs;
    if (Array.isArray(rawPairs)) {
      for (const pair of rawPairs) {
        if (!Array.isArray(pair) || pair.length < 2) continue;
        const firstId = String(pair[0] ?? '').trim();
        const secondId = String(pair[1] ?? '').trim();
        if (!firstId || !secondId) continue;
        if (!blockIdSet.has(firstId) || !blockIdSet.has(secondId)) continue;
        supersetByFirst.set(firstId, secondId);
      }
    }

    for (let i = 0; i < blockExerciseDetails.length; i++) {
      const first = blockExerciseDetails[i];
      const second = blockExerciseDetails[i + 1];
      if (second && supersetByFirst.get(first.id) === second.id) {
        const maxSets = Math.max(first.sets, second.sets);
        for (let s = 0; s < maxSets; s++) {
          if (s < first.sets) steps.push({ globalIdx: first.globalIdx, setIndex: s });
          if (s < second.sets) steps.push({ globalIdx: second.globalIdx, setIndex: s });
        }
        i += 1;
        continue;
      }
      for (let s = 0; s < first.sets; s++) {
        steps.push({ globalIdx: first.globalIdx, setIndex: s });
      }
    }
    return steps;
  }, [blockExerciseDetails, currentBlock, roundCount]);

  const emomSteps = React.useMemo(() => {
    if (!isEmom || !methodDurationMin) return [] as Step[];
    const totalDurationMs = Math.max(0, methodDurationMin * 60 * 1000);
    const intervalMs = emomIntervalMs > 0 ? emomIntervalMs : 60 * 1000;
    const ids = blockExerciseIndices;
    if (!ids.length || totalDurationMs === 0) return [] as Step[];
    const steps: Step[] = [];
    // Prefer the explicit round count. rounds × interval can be a fraction of a
    // minute (5 × 0:45 = 3.75 min), which durationMin rounds away, so deriving
    // the count from the duration would drop or add an interval.
    const explicitRounds = Number((currentBlock as any)?.methodMeta?.rounds);
    const intervalsCount =
      Number.isFinite(explicitRounds) && explicitRounds > 0
        ? Math.floor(explicitRounds)
        : Math.max(1, Math.ceil(totalDurationMs / intervalMs));
    for (let i = 0; i < intervalsCount; i += 1) {
      if (isComplexEmom) {
        // One step per interval covering the whole block. It is anchored to the
        // first exercise so everything reading `globalIdx` still resolves, but
        // the round list is what gets rendered — every movement, once per round.
        steps.push({ globalIdx: ids[0], setIndex: i });
        continue;
      }
      const idx = i % ids.length;
      const occurrence = Math.floor(i / ids.length);
      steps.push({ globalIdx: ids[idx], setIndex: occurrence });
    }
    return steps;
  }, [blockExerciseIndices, currentBlock, emomIntervalMs, isEmom, isComplexEmom, methodDurationMin]);

  const emomSetCountByGlobalIdx = React.useMemo(() => {
    const counts = new Map<number, number>();
    if (!isEmom) return counts;
    if (isComplexEmom) {
      // Every movement is performed in every interval, so each does one set per
      // round. The steps only name the anchor exercise, so count rounds instead.
      for (const globalIdx of blockExerciseIndices) counts.set(globalIdx, emomSteps.length);
      return counts;
    }
    for (const step of emomSteps) {
      const next = step.setIndex + 1;
      const prev = counts.get(step.globalIdx) ?? 0;
      if (next > prev) counts.set(step.globalIdx, next);
    }
    return counts;
  }, [blockExerciseIndices, emomSteps, isEmom, isComplexEmom]);

  const blockPosByGlobalIdx = React.useMemo(() => {
    const map = new Map<number, number>();
    blockExerciseIndices.forEach((globalIdx, i) => {
      map.set(globalIdx, i);
    });
    return map;
  }, [blockExerciseIndices]);
  const methodStartOffsetRef = React.useRef(0);
  const methodElapsedMs = Math.max(0, workoutMs - methodStartOffsetRef.current);
  const effectiveMethodElapsedMs =
    useRoundWorkflow && roundWorkflowDoneAtMs != null ? roundWorkflowDoneAtMs : methodElapsedMs;

  const emomStepIdx = React.useMemo(() => {
    if (!isEmom || emomSteps.length === 0) return 0;
    const intervalMs = emomIntervalMs > 0 ? emomIntervalMs : 60 * 1000;
    const idx = Math.floor(methodElapsedMs / intervalMs);
    if (!Number.isFinite(idx)) return 0;
    return Math.min(Math.max(idx, 0), emomSteps.length - 1);
  }, [emomIntervalMs, emomSteps, isEmom, methodElapsedMs]);

  React.useEffect(() => {
    if (!isEmom && stepIdx >= blockSteps.length) {
      setStepIdx(0);
    }
  }, [blockSteps, stepIdx, isEmom]);

  const activeSteps = isEmom ? emomSteps : blockSteps;
  const activeStepIdx = isEmom ? emomStepIdx : stepIdx;

  React.useEffect(() => {
    setSetDoneConfirmed(false);
  }, [activeStepIdx, blockIdx]);

  const currentStep = activeSteps[activeStepIdx] ?? null;
  const nextStep = activeSteps[activeStepIdx + 1] ?? null;

  const currentGlobalIdx = currentStep ? currentStep.globalIdx : null;
  const setNo = currentStep ? currentStep.setIndex + 1 : 1;

  const currentBlockPos =
    currentGlobalIdx != null ? blockPosByGlobalIdx.get(currentGlobalIdx) ?? null : null;
  const nextExerciseGlobalIdx = React.useMemo(() => {
    if (nextStep && nextStep.globalIdx !== currentGlobalIdx) return nextStep.globalIdx;
    if (currentBlockPos == null) return null;
    const nextIdx = blockExerciseIndices[currentBlockPos + 1];
    return typeof nextIdx === 'number' ? nextIdx : null;
  }, [nextStep, currentGlobalIdx, currentBlockPos, blockExerciseIndices]);
  const nextBlockPos =
    nextExerciseGlobalIdx != null ? blockPosByGlobalIdx.get(nextExerciseGlobalIdx) ?? null : null;

  const current = currentGlobalIdx != null ? exList[currentGlobalIdx] || null : null;
  const next = nextExerciseGlobalIdx != null ? exList[nextExerciseGlobalIdx] || null : null;
  const nextStepExercise = nextStep ? exList[nextStep.globalIdx] || null : null;
  const nextDisplayExercise = nextStepExercise ?? next ?? null;
  const nextStepName = nextDisplayExercise
    ? (nextDisplayExercise.nameRaw ?? (nextDisplayExercise as any)?.raw ?? null)
    : null;
  const repsPerSet = Array.isArray(current?.repsPerSet) ? current?.repsPerSet : null;
  const targetSetsRaw = current?.targetSets ?? (repsPerSet ? repsPerSet.length : 1);
  const targetSetsValue = Number(targetSetsRaw);
  const targetSetsCount =
    Number.isFinite(targetSetsValue) && targetSetsValue > 0
      ? Math.floor(targetSetsValue)
      : 1;
  const emomTargetSets =
    isEmom && currentGlobalIdx != null
      ? emomSetCountByGlobalIdx.get(currentGlobalIdx) ?? 1
      : null;
  const displayTargetSets = isEmom
    ? Math.max(targetSetsCount, emomTargetSets ?? 1, setNo)
    : targetSetsCount;
  const currentExerciseId =
    (current as any)?.exerciseId ?? current?.id ?? null;
  const currentExerciseLabel = current?.nameRaw ?? (current as any)?.raw ?? '';
  const currentWodRxLoad = getWodRxForExercise(
    (currentBlock as any)?.methodMeta,
    currentExerciseId
  );
  const currentWodRxNote = getWodRxNote((currentBlock as any)?.methodMeta);
  const currentWodRxSummary =
    currentWodRxLoad && currentWodRxNote && currentWodRxLoad !== currentWodRxNote
      ? `${currentWodRxLoad} | ${currentWodRxNote}`
      : currentWodRxLoad ?? currentWodRxNote;
  const isCurrentShuttleSprintExercise = isShuttleSprintExerciseLabel(currentExerciseLabel);
  const currentHasPlannedDistance =
    hasPositiveNumber((current as any)?.plannedWeights) ||
    hasPositiveNumber((current as any)?.weights);
  const currentUsesRepsDistanceModel =
    isCurrentShuttleSprintExercise && currentHasPlannedDistance;
  const isCurrentDistanceExercise =
    isDistanceExerciseLabel(currentExerciseLabel) &&
    (!isCurrentShuttleSprintExercise || !currentUsesRepsDistanceModel);
  const isDropsetExercise =
    typeof currentExerciseId === 'string' && dropsetExerciseIds.has(currentExerciseId);
  const isCurrentDropsetSet =
    isDropsetExercise &&
    targetSetsCount > 1 &&
    setNo === targetSetsCount;
  const currentRepValue =
    currentGlobalIdx != null
      ? resolveRepForSet(repOverrides[currentGlobalIdx], repsPerSet, setNo - 1)
      : null;
  const currentRepUnit = getRepUnit(currentExerciseLabel, {
    shuttleUsesRepsDistance: currentUsesRepsDistanceModel,
  });
  const repLabel =
    currentRepUnit === 'sec'
      ? t('Hold (sec)', 'Hold (sec)')
      : isCurrentDistanceExercise
        ? t('Distance (m)', 'Distance (m)')
        : t('Reps', 'Reps');
  const secondaryMetricLabel =
    isCurrentShuttleSprintExercise && currentUsesRepsDistanceModel
      ? t('Distance (m)', 'Distance (m)')
      : t('Weight (kg)', 'Weight (kg)');
  const secondaryMetricUnit =
    isCurrentShuttleSprintExercise && currentUsesRepsDistanceModel ? 'm' : 'kg';
  const currentRepDisplay =
    isCurrentDropsetSet && typeof currentRepValue !== 'number'
      ? 'X'
      : typeof currentRepValue === 'number'
        ? String(currentRepValue)
        : '';
  const nextDetail = React.useMemo(() => {
    if (!nextStep || !nextStepExercise) return '';
    const nextGlobalIdx = nextStep.globalIdx;
    const setIndex = nextStep.setIndex;
    const name =
      nextStepExercise?.nameRaw ?? (nextStepExercise as any)?.raw ?? '';
    const nextExerciseId =
      (nextStepExercise as any)?.exerciseId ?? nextStepExercise?.id ?? null;
    const repsPerSet = Array.isArray((nextStepExercise as any)?.repsPerSet)
      ? (nextStepExercise as any).repsPerSet
      : null;
    const targetSetsRaw =
      (nextStepExercise as any)?.targetSets ?? (repsPerSet ? repsPerSet.length : 1);
    const targetSetsValue = Number(targetSetsRaw);
    const targetSetsCount =
      Number.isFinite(targetSetsValue) && targetSetsValue > 0
        ? Math.floor(targetSetsValue)
        : 1;
    const isDropsetExercise =
      typeof nextExerciseId === 'string' && dropsetExerciseIds.has(nextExerciseId);
    const isDropsetSet =
      isDropsetExercise &&
      targetSetsCount > 1 &&
      setIndex + 1 === targetSetsCount;
    const repValue = resolveRepForSet(repOverrides[nextGlobalIdx], repsPerSet, setIndex);
    const repDisplay =
      isDropsetSet && typeof repValue !== 'number'
        ? 'X'
        : typeof repValue === 'number'
          ? String(repValue)
          : '';
    const isShuttleSprint = isShuttleSprintExerciseLabel(name);

    const weightsArr =
      Array.isArray((nextStepExercise as any)?.plannedWeights)
        ? (nextStepExercise as any).plannedWeights
        : Array.isArray((nextStepExercise as any)?.weights)
          ? (nextStepExercise as any).weights
          : [];
    const shuttleUsesRepsDistance =
      isShuttleSprint && hasPositiveNumber(weightsArr);
    const repUnit = getRepUnit(name, {
      shuttleUsesRepsDistance,
    });
    const repText = repDisplay ? `${repDisplay} ${repUnit}` : '';
    const weightRaw = weightsArr?.[setIndex];
    const weight =
      typeof weightRaw === 'number' && weightRaw > 0 ? weightRaw : null;
    const weightText = weight != null ? `@ ${weight} kg` : '';
    const distanceText = weight != null ? `${weight} m` : '';
    return isShuttleSprint && shuttleUsesRepsDistance
      ? repText && distanceText
        ? `${repText} x ${distanceText}`
        : repText || distanceText
      : [repText, weightText].filter(Boolean).join(' ');
  }, [nextStep, nextStepExercise, dropsetExerciseIds, repOverrides]);

  // Format an upcoming step as "<Exercise Name>" + "8 reps @ 80 kg" detail.
  const formatStepLine = React.useCallback(
    (step: Step): { name: string; detail: string } | null => {
      const ex = exList[step.globalIdx];
      if (!ex) return null;
      const setIndex = step.setIndex;
      const name = ex?.nameRaw ?? (ex as any)?.raw ?? '';
      const exerciseId = (ex as any)?.exerciseId ?? ex?.id ?? null;
      const repsPerSet = Array.isArray((ex as any)?.repsPerSet) ? (ex as any).repsPerSet : null;
      const targetSetsRaw = (ex as any)?.targetSets ?? (repsPerSet ? repsPerSet.length : 1);
      const targetSetsValue = Number(targetSetsRaw);
      const targetSetsCount =
        Number.isFinite(targetSetsValue) && targetSetsValue > 0 ? Math.floor(targetSetsValue) : 1;
      const isDropsetExercise = typeof exerciseId === 'string' && dropsetExerciseIds.has(exerciseId);
      const isDropsetSet = isDropsetExercise && targetSetsCount > 1 && setIndex + 1 === targetSetsCount;
      const repValue = resolveRepForSet(repOverrides[step.globalIdx], repsPerSet, setIndex);
      const repDisplay =
        isDropsetSet && typeof repValue !== 'number'
          ? 'X'
          : typeof repValue === 'number'
            ? String(repValue)
            : '';
      const isShuttleSprint = isShuttleSprintExerciseLabel(name);
      const weightsArr = Array.isArray((ex as any)?.plannedWeights)
        ? (ex as any).plannedWeights
        : Array.isArray((ex as any)?.weights)
          ? (ex as any).weights
          : [];
      const shuttleUsesRepsDistance = isShuttleSprint && hasPositiveNumber(weightsArr);
      const repUnit = getRepUnit(name, { shuttleUsesRepsDistance });
      const repText = repDisplay ? `${repDisplay} ${repUnit}` : '';
      const weightRaw = weightsArr?.[setIndex];
      const weight = typeof weightRaw === 'number' && weightRaw > 0 ? weightRaw : null;
      const weightText = weight != null ? `@ ${weight} kg` : '';
      const distanceText = weight != null ? `${weight} m` : '';
      const detail =
        isShuttleSprint && shuttleUsesRepsDistance
          ? repText && distanceText
            ? `${repText} x ${distanceText}`
            : repText || distanceText
          : [repText, weightText].filter(Boolean).join(' ');
      return { name, detail };
    },
    [exList, dropsetExerciseIds, repOverrides]
  );

  // The next 2 sets after the current one, for the "Next up" box in the middle.
  const upcomingSteps = React.useMemo(
    () =>
      activeSteps
        .slice(activeStepIdx + 1, activeStepIdx + 3)
        .map((s) => formatStepLine(s))
        .filter(Boolean) as Array<{ name: string; detail: string }>,
    [activeSteps, activeStepIdx, formatStepLine]
  );

  const repPlaceholder = isCurrentDropsetSet ? 'X' : repLabel;
  const isLastStepInBlock = !nextStep;
  const isNextSameExercise = nextStep?.globalIdx === currentGlobalIdx;
  const hasNextMethod = blockIdx < methodBlocks.length - 1;
  const isTransitionPending = pendingNextMethodIdx != null;
  const controlsDisabled =
    waitingForCoachStart || ((countdown > 0 && countdownKind !== 'rest') || isTransitionPending);
  const actionDisabled = waitingForCoachStart || countdown > 0 || isTransitionPending;
  const pauseDisabled = waitingForCoachStart || countdown > 0;
  const pauseMode =
    (currentBlock as any)?.methodMeta?.pauseMode === 'down' ? 'down' : 'up';
  const pauseDurationRaw = Number((currentBlock as any)?.methodMeta?.pauseDurationSec);
  const pauseDurationSec =
    Number.isFinite(pauseDurationRaw) && pauseDurationRaw > 0
      ? Math.floor(pauseDurationRaw)
      : null;
  const restCountdownActive = countdown > 0 && countdownKind === 'rest';
  const restFinalSeconds = restCountdownActive && countdown <= 5;
  // Says which set it belongs to, now that a second, whole-workout pause sits
  // next to it in the footer — "Pause time" on its own no longer distinguishes
  // the two. Reads "Set pause" or "Round pause" depending on the method.
  const pauseLabel =
    restCountdownActive || (pauseMode === 'down' && !paused)
      ? 'Rest time'
      : `${roundLabel} pause`;
  const pauseDisplayMs = restCountdownActive
    ? countdown * 1000
    : pauseMode === 'down' && !paused
      ? (pauseDurationSec ? pauseDurationSec * 1000 : 0)
      : pauseSw.elapsed;
  // The left-hand number: every pause since the first set, across exercises and
  // across method blocks, still ticking while paused. Unlike `pauseDisplayMs` it
  // is never substituted by the rest countdown — a planned rest is not a pause,
  // and letting the two share one readout is what made "Pause time" ambiguous in
  // 'down' mode in the first place.
  const totalPauseMs = pauseTotalSw.elapsed;
  const currentRoundNo = useRoundList
    ? Math.min(Math.max(setNo, 1), roundCount || 1)
    : setNo;
  const currentRoundIndex = Math.max(currentRoundNo - 1, 0);
  const isLastRound = useRoundList && !!roundCount && currentRoundNo >= roundCount;

  const roundStepMeta = React.useMemo(() => {
    if (!useRoundWorkflow) return { start: 0, count: 0 };
    let start = -1;
    let count = 0;
    for (let i = 0; i < blockSteps.length; i += 1) {
      const step = blockSteps[i];
      if (step.setIndex === currentRoundIndex) {
        if (start < 0) start = i;
        count += 1;
      } else if (start >= 0) {
        break;
      }
    }
    if (start < 0) start = Math.min(stepIdx, blockSteps.length);
    return { start, count };
  }, [blockSteps, currentRoundIndex, stepIdx, useRoundWorkflow]);

  const roundStartIdx = roundStepMeta.start;
  const roundStepCount = roundStepMeta.count;

  React.useEffect(() => {
    if (!useRoundWorkflow) return;
    if (roundStartIdx >= 0 && stepIdx !== roundStartIdx) {
      setStepIdx(roundStartIdx);
    }
  }, [roundStartIdx, stepIdx, useRoundWorkflow]);

  const pendingBlock = isTransitionPending
    ? methodBlocks[Math.min(pendingNextMethodIdx ?? 0, methodBlocks.length - 1)]
    : null;
  const pendingTiming = getBlockTiming(pendingBlock);
  const pendingDurationMin = pendingTiming.durationMin;
  const pendingMode = pendingTiming.mode;
  const pendingIsEmom = pendingMode === 'emom';
  const pendingIsAmrap =
    !pendingIsEmom && typeof pendingDurationMin === 'number' && pendingDurationMin > 0;
  const pendingEmomTotal = pendingIsEmom
    ? formatEmomTotal(pendingTiming.rounds, pendingTiming.intervalSec, pendingDurationMin)
    : null;
  const pendingLadderRounds = Array.isArray((pendingBlock as any)?.methodMeta?.ladder)
    ? (pendingBlock as any).methodMeta.ladder.filter(
        (v: any) => Number.isFinite(Number(v)) && Number(v) > 0
      )
    : [];
  const pendingRoundCount = pendingLadderRounds.length
    ? pendingLadderRounds.length
    : normalizeRounds((pendingBlock as any)?.methodMeta?.rounds) ?? 0;
  // A round count is the headline only when nothing timed outranks it: an EMOM
  // and an AMRAP both store `rounds` too, and there the duration is the thing
  // the athlete is about to be held to.
  const pendingLabelIsRounds = !pendingEmomTotal && !pendingIsAmrap && pendingRoundCount > 0;
  const pendingLabel =
    pendingNextMethodIdx != null
      ? pendingEmomTotal
        ? `EMOM ${pendingEmomTotal}`
        : pendingIsAmrap
          ? `AMRAP ${pendingDurationMin} min`
          : pendingLabelIsRounds
            ? `${pendingRoundCount} ${t('Rounds', 'Rounds')}`
            : `Method ${pendingNextMethodIdx + 1}`
      : null;
  const pendingTimeCapSec = (pendingBlock as any)?.methodMeta?.forTime
    ? getTimeCapSec((pendingBlock as any)?.methodMeta)
    : null;
  // What the headline had no room for. The EMOM total is rounds × interval, so
  // both factors are worth naming separately — "EMOM 10 min" alone doesn't say
  // whether that is 10 × 1:00 or 20 × 0:30.
  const pendingStructure = React.useMemo(() => {
    const chips: string[] = [];
    if (pendingIsEmom && pendingTiming.intervalSec) {
      const sec = pendingTiming.intervalSec;
      const every = sec % 60 === 0 ? `${sec / 60} min` : fmtTime(sec * 1000);
      chips.push(`${every} ${t('Interval', 'interval')}`);
    }
    if (pendingRoundCount > 0 && !pendingLabelIsRounds) {
      chips.push(`${pendingRoundCount} ${t('Rounds', 'Rounds')}`);
    }
    if (pendingTimeCapSec) {
      chips.push(`${t('Time cap', 'Time cap')} ${fmtTime(pendingTimeCapSec * 1000)}`);
    }
    return chips;
  }, [
    pendingIsEmom,
    pendingTiming.intervalSec,
    pendingRoundCount,
    pendingLabelIsRounds,
    pendingTimeCapSec,
    t,
  ]);

  /**
   * The next block's movements, read off before it starts, each with its
   * sets/reps/load. (Its equipment is `pendingEquipment`, further down.)
   *
   * Deliberately built from the same pieces the live views use — the pending
   * block's own rows via `blockIndicesByBlock`, `resolveRepForSet` for the reps
   * (so a live rep edit and the round-repeat rule both carry over) and
   * `getRepUnit` for the unit — so the preview can't drift from the screen that
   * replaces it one tap later.
   */
  const pendingPreview = React.useMemo(() => {
    if (pendingNextMethodIdx == null) return null;
    const indices = blockIndicesByBlock[pendingNextMethodIdx] ?? [];
    const exercises = indices
      .map((globalIdx) => {
        const ex: any = exList[globalIdx];
        if (!ex) return null;
        const name = ex?.nameRaw ?? ex?.raw ?? '';
        const exerciseId = ex?.exerciseId ?? ex?.id ?? null;
        const repsPerSet = Array.isArray(ex?.repsPerSet) ? ex.repsPerSet : null;
        const targetSetsValue = Number(ex?.targetSets ?? (repsPerSet ? repsPerSet.length : 1));
        const targetSets =
          Number.isFinite(targetSetsValue) && targetSetsValue > 0
            ? Math.floor(targetSetsValue)
            : 1;
        const isDropset = typeof exerciseId === 'string' && dropsetExerciseIds.has(exerciseId);
        const weightsArr: any[] = Array.isArray(ex?.plannedWeights)
          ? ex.plannedWeights
          : Array.isArray(ex?.weights)
            ? ex.weights
            : [];
        const isShuttleSprint = isShuttleSprintExerciseLabel(name);
        const shuttleUsesRepsDistance = isShuttleSprint && hasPositiveNumber(weightsArr);
        const repUnit = getRepUnit(name, { shuttleUsesRepsDistance });
        // One entry per set actually coming up. A round-based block repeats
        // every movement each round, so there it is the rounds that decide the
        // count — the same Math.max rule blockSteps uses to build the steps.
        const setCount = pendingRoundCount
          ? Math.max(pendingRoundCount, targetSets)
          : targetSets;
        const reps: string[] = [];
        const loads: string[] = [];
        for (let i = 0; i < setCount; i += 1) {
          const repValue = resolveRepForSet(repOverrides[globalIdx], repsPerSet, i);
          reps.push(
            typeof repValue === 'number'
              ? String(repValue)
              : isDropset && setCount > 1 && i + 1 === setCount
                ? 'X'
                : ''
          );
          const weightRaw = weightsArr?.[i];
          loads.push(typeof weightRaw === 'number' && weightRaw > 0 ? String(weightRaw) : '');
        }
        const repText = collapsePerSet(reps);
        const loadText = collapsePerSet(loads);
        const parts: string[] = [];
        // The round or interval count is named once, above. Repeating it as
        // "5 sets" on every line would be noise, and for a rotating EMOM it
        // would be a different number than the one in the header.
        if (!pendingRoundCount && !pendingIsEmom) {
          parts.push(`${targetSets} ${targetSets === 1 ? t('set', 'set') : t('sets', 'sets')}`);
        }
        if (repText) parts.push(`${repText} ${repUnit}`);
        if (loadText) parts.push(`${loadText} ${shuttleUsesRepsDistance ? 'm' : 'kg'}`);
        return { key: `${globalIdx}-${name}`, name, detail: parts.join(' · ') };
      })
      .filter(Boolean) as Array<{ key: string; name: string; detail: string }>;
    // Equipment is NOT on these rows (see pendingEquipment) — ids only, so the
    // catalog can be consulted once it has loaded.
    return {
      exercises,
      exerciseIds: indices.map(
        (i) => (exList[i] as any)?.exerciseId ?? exList[i]?.id ?? null
      ),
    };
  }, [
    pendingNextMethodIdx,
    blockIndicesByBlock,
    exList,
    repOverrides,
    dropsetExerciseIds,
    pendingRoundCount,
    pendingIsEmom,
    t,
  ]);

  // Catalog tags are English keys; the picker's German lives under the pretty
  // label (`equipment:Pull-up bar`), same as PreparePanel. The joined
  // "either" entry is split so both halves get translated.
  const equipmentLabel = React.useCallback(
    (token: string) =>
      token
        .split('|')
        .map((tag) => {
          const pretty = prettyEquipment(tag);
          return t(`equipment:${pretty}`, pretty);
        })
        .join(' / '),
    [t]
  );

  const amrapDurationMin = isAmrap ? methodDurationMin : null;
  const emomDurationMin = isEmom ? methodDurationMin : null;
  const amrapTargetMs =
    typeof amrapDurationMin === 'number' && amrapDurationMin > 0
      ? amrapDurationMin * 60 * 1000
      : null;
  // Same reason as emomSteps: rounds × interval is exact, durationMin is rounded.
  const emomTargetMs = isEmom && emomSteps.length > 0
    ? emomSteps.length * emomIntervalMs
    : typeof emomDurationMin === 'number' && emomDurationMin > 0
      ? emomDurationMin * 60 * 1000
      : null;
  const timeCapTargetMs =
    typeof timeCapSec === 'number' && timeCapSec > 0
      ? timeCapSec * 1000
      : null;

  const methodTargetMs = amrapTargetMs ?? emomTargetMs ?? timeCapTargetMs;
  const methodRemainingMs =
    typeof methodTargetMs === 'number'
      ? Math.max(0, methodTargetMs - effectiveMethodElapsedMs)
      : null;
  // Countdown is rounded UP so it stays in lockstep with the count-up set timer
  // (which floors): e.g. at 30s elapsed the total reads 19:30 and the set 0:30,
  // not 0:29. Count-up displays keep their natural floor via fmtTime.
  const workoutDisplayMs =
    typeof methodRemainingMs === 'number'
      ? Math.ceil(methodRemainingMs / 1000) * 1000
      : methodElapsedMs;
  // Seconds left in the CURRENT EMOM interval (counts down 1:00 → 0:00 each minute),
  // ceiled to whole seconds so it matches the total countdown.
  const emomIntervalRemainingMs =
    isEmom && emomIntervalMs > 0
      ? Math.ceil((emomIntervalMs - (methodElapsedMs % emomIntervalMs)) / 1000) * 1000
      : 0;
  const timeCapOvertimeMs =
    timeCapTargetMs != null && effectiveMethodElapsedMs > timeCapTargetMs
      ? effectiveMethodElapsedMs - timeCapTargetMs
      : 0;
  const amrapNotifiedRef = React.useRef(false);
  const emomFinishedRef = React.useRef(false);
  const timeCapNotifiedRef = React.useRef(false);
  const emomIntervalRef = React.useRef<number | null>(null);
  const [amrapRounds, setAmrapRounds] = React.useState(0);
  const [amrapRoundTimes, setAmrapRoundTimes] = React.useState<number[]>([]);
  const amrapRoundTimesRef = React.useRef<AmrapRoundTimes[]>([]);
  const lastRoundMarkRef = React.useRef<Record<string, number>>({});
  const amrapTimeUp = !!amrapTargetMs && methodElapsedMs >= amrapTargetMs;
  const emomTimeUp = !!emomTargetMs && methodElapsedMs >= emomTargetMs;
  const timeCapTimeUp = !!timeCapTargetMs && effectiveMethodElapsedMs >= timeCapTargetMs;
  const amrapResultsRef = React.useRef<Array<{ blockId: string; durationMin: number; rounds: number }>>([]);
  const blockExercises = React.useMemo(
    () => blockExerciseIndices.map((i) => exList[i]).filter(Boolean),
    [blockExerciseIndices, exList]
  );

  React.useEffect(() => {
    if (!currentBlockId) return;
    const existing = amrapRoundTimesRef.current.find((r) => r.blockId === currentBlockId);
    const times = existing?.roundTimesMs ?? [];
    setAmrapRoundTimes(times);
    if (times.length > 0) {
      lastRoundMarkRef.current[currentBlockId] = sumMs(times);
    } else if (lastRoundMarkRef.current[currentBlockId] == null) {
      lastRoundMarkRef.current[currentBlockId] = 0;
    }
  }, [currentBlockId]);

  const recordRoundTime = React.useCallback(() => {
    if (!currentBlockId) return null;
    const elapsed = Math.max(0, methodElapsedMs);
    const lastMark = lastRoundMarkRef.current[currentBlockId] ?? 0;
    const delta = Math.max(0, elapsed - lastMark);
    lastRoundMarkRef.current[currentBlockId] = elapsed;
    setAmrapRoundTimes((prev) => {
      const nextTimes = [...prev, delta];
      setAmrapRounds(nextTimes.length);
      const existing = amrapRoundTimesRef.current.find((r) => r.blockId === currentBlockId);
      if (existing) {
        existing.roundTimesMs = nextTimes;
      } else {
        amrapRoundTimesRef.current.push({ blockId: currentBlockId, roundTimesMs: nextTimes });
      }
      return nextTimes;
    });
    return delta;
  }, [currentBlockId, methodElapsedMs]);

  // The round currently being worked on. Reads the same mark recordRoundTime
  // will subtract, so the live row counts up to exactly the value that gets
  // recorded on the next "+ Round". methodElapsedMs ticks, so this re-renders.
  // Falls back to the sum of recorded rounds for the frame before the
  // block-change effect has populated the ref.
  const amrapLastRoundMark =
    (currentBlockId ? lastRoundMarkRef.current[currentBlockId] : undefined) ??
    sumMs(amrapRoundTimes);
  const amrapCurrentRoundMs = Math.max(0, methodElapsedMs - amrapLastRoundMark);
  const amrapPrevRoundMs =
    amrapRoundTimes.length > 0 ? amrapRoundTimes[amrapRoundTimes.length - 1]! : null;
  // Only once you are actually slower than the last round. A negative "delta"
  // mid-round would claim you are ahead on a round you have not finished.
  const amrapBehindMs =
    amrapPrevRoundMs != null && amrapCurrentRoundMs > amrapPrevRoundMs
      ? amrapCurrentRoundMs - amrapPrevRoundMs
      : null;
  const showAmrapCurrentRound = !!amrapTargetMs && !amrapTimeUp;

  const roundTimesList =
    amrapRoundTimes.length > 0 || showAmrapCurrentRound ? (
      <div className="mt-3 grid gap-1 text-xs">
        {amrapRoundTimes.map((ms, idx) => {
          const prev = idx > 0 ? amrapRoundTimes[idx - 1] : null;
          const delta = prev != null ? ms - prev : null;
          const deltaAbs = delta != null ? Math.abs(delta) : null;
          const deltaText =
            delta == null
              ? ''
              : `${delta >= 0 ? '+' : '-'}${fmtTime(deltaAbs || 0)} min`;
          const deltaClass =
            delta == null
              ? ''
              : delta > 0
                ? 'text-rose-600'
                : delta < 0
                  ? 'text-emerald-600'
                  : 'text-gray-500';
          return (
            <div key={`round-${idx}`} className="flex items-center justify-between opacity-80">
              <span>{t('Round', 'Round')} {idx + 1}</span>
              <span className="tabular-nums">
                {fmtTime(ms)}
                {delta != null && <span className={`ml-2 ${deltaClass}`}>{deltaText}</span>}
              </span>
            </div>
          );
        })}

        {/* Live row for the round in progress — same reading as "Next up in". */}
        {showAmrapCurrentRound && (
          <div className="flex items-center justify-between font-semibold text-brand-600 dark:text-brand-400">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" aria-hidden />
              {t('Round', 'Round')} {amrapRoundTimes.length + 1}
            </span>
            <span className="tabular-nums">
              {fmtTime(amrapCurrentRoundMs)}
              {amrapBehindMs != null && (
                <span className="ml-2 text-rose-600">+{fmtTime(amrapBehindMs)} min</span>
              )}
            </span>
          </div>
        )}
      </div>
    ) : null;

  const emomIntervalLabel = fmtTime(emomIntervalMs);
  // Round count reads truer than the rounded minutes when the interval isn't 1:00.
  const emomSummaryLabel = emomSteps.length
    ? `EMOM ${emomSteps.length} × ${fmtTime(emomIntervalMs)}`
    : emomDurationMin
      ? `EMOM ${emomDurationMin} min`
      : 'EMOM';

  const methodSubtitle = React.useMemo(() => {
    const raw =
      (training as any)?.method?.raw ??
      training.method?.name ??
      training.method?.type ??
      '';
    if (isEmom) {
      const total = formatEmomTotal(currentTiming.rounds, currentTiming.intervalSec, methodDurationMin);
      if (total) return `EMOM ${total}`;
    }
    if (isAmrap && methodDurationMin) return `AMRAP ${methodDurationMin} min`;
    if (forTimeEnabled) {
      return timeCapSec ? `For Time (cap ${fmtTime(timeCapSec * 1000)})` : 'For Time';
    }
    if (raw) return raw;
    if (methodBlocks.length > 1) return `Method ${blockIdx + 1}`;
    return '';
  }, [training, isEmom, isAmrap, methodDurationMin, currentTiming.rounds, currentTiming.intervalSec, methodBlocks.length, blockIdx, forTimeEnabled, timeCapSec]);

  React.useEffect(() => {
    if (!requiresGlobalStart || !plannedId) return;
    // Refresh the server-clock offset so this coach workout's timer is aligned
    // to server time (not this device's possibly-skewed clock).
    void syncServerClock();
    void loadJoinState({ silent: true });

    // Realtime: when the coach broadcasts a start, refetch the authoritative
    // join-state immediately (the API stays the source of truth).
    const unsubscribe = subscribeGlobalStart(plannedId, () => {
      void syncServerClock();
      void loadJoinState({ silent: true });
    });

    if (!waitingForCoachStart) return () => unsubscribe();

    // Fallback poll in case a broadcast is missed (reconnect/offline). Kept at
    // 3s so it still lands inside the 5s start-countdown if realtime is down.
    // Only runs during the short pre-start waiting window, then stops.
    const id = window.setInterval(() => {
      void loadJoinState({ silent: true });
    }, 3000);
    return () => { unsubscribe(); window.clearInterval(id); };
  }, [loadJoinState, requiresGlobalStart, waitingForCoachStart, plannedId]);

  React.useEffect(() => {
    if (!amrapTargetMs) {
      amrapNotifiedRef.current = false;
      return;
    }
    if (methodElapsedMs < amrapTargetMs) {
      amrapNotifiedRef.current = false;
      return;
    }
    if (!amrapNotifiedRef.current) {
      amrapNotifiedRef.current = true;
      showToast('AMRAP time is up');
      cueEnd();
    }
  }, [amrapTargetMs, methodElapsedMs]);

  React.useEffect(() => {
    if (!timeCapTargetMs) {
      timeCapNotifiedRef.current = false;
      return;
    }
    if (effectiveMethodElapsedMs < timeCapTargetMs) {
      timeCapNotifiedRef.current = false;
      return;
    }
    if (!timeCapNotifiedRef.current) {
      timeCapNotifiedRef.current = true;
      showToast('Time cap is up');
      cueEnd();
    }
  }, [effectiveMethodElapsedMs, timeCapTargetMs]);

  // ---------- Progress (weights & recorded time) ----------
  // We try to preload per-set planned weights if available:
  // - from `exercise.plannedWeights` or `exercise.weights` (if you passed them in startTraining)
  const initProgressForExercise = (ex: any): ExerciseProgress => {
    const repsRaw = Array.isArray(ex?.repsPerSet) ? ex.repsPerSet : [];
    const count = ex?.targetSets ?? (repsRaw.length || 1);
    const plannedW: number[] | undefined =
      (ex && Array.isArray((ex as any).plannedWeights) && (ex as any).plannedWeights) ||
      (ex && Array.isArray((ex as any).weights) && (ex as any).weights) ||
      undefined;

    return {
      sets: Array.from({ length: count }, (_, i) => ({
        weight: plannedW?.[i] ?? null,
        timeMs: null,
      })),
    };
  };

  const [progress, setProgress] = React.useState<ExerciseProgress[]>(
    exList.map(initProgressForExercise)
  );

  // Reinitialize progress if the training/exercise list changes
  React.useEffect(() => {
    if (resumeState) {
      const nextProgress =
        Array.isArray(resumeState.progress) && resumeState.progress.length === exList.length
          ? resumeState.progress
          : exList.map(initProgressForExercise);
      const nextRepOverrides =
        Array.isArray(resumeState.repOverrides) && resumeState.repOverrides.length === exList.length
          ? resumeState.repOverrides
          : exList.map(initRepOverridesForExercise);
      setProgress(nextProgress);
      setRepOverrides(nextRepOverrides);
    } else {
      setProgress(exList.map(initProgressForExercise));
      setRepOverrides(exList.map(initRepOverridesForExercise));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [training]);

  React.useEffect(() => {
    workoutMsRef.current = workoutMs;
  }, [workoutMs]);

  React.useEffect(() => {
    setEditTarget(null);
  }, [blockIdx]);

  // Weight input for ACTIVE set
  const activeWeight = React.useMemo<number | ''>(() => {
    if (currentGlobalIdx == null) return '';
    const rec = progress[currentGlobalIdx]?.sets?.[setNo - 1];
    return rec?.weight ?? '';
  }, [progress, currentGlobalIdx, setNo]);

  const roundExercises = React.useMemo(() => {
    if (!useRoundList) return [] as Array<{ globalIdx: number; name: string; detail: string }>;
    const roundIdx = currentRoundIndex;
    return blockExerciseIndices
      .map((globalIdx, exIdx) => {
        const ex = exList[globalIdx];
        if (!ex) return null;
        const name = ex?.nameRaw ?? (ex as any)?.raw ?? `Exercise ${exIdx + 1}`;
        const repsPerSet = Array.isArray(ex?.repsPerSet) ? ex.repsPerSet : null;
        const targetSetsRaw = ex?.targetSets ?? (repsPerSet ? repsPerSet.length : 1);
        const targetSetsValue = Number(targetSetsRaw);
        const targetSetsCount =
          Number.isFinite(targetSetsValue) && targetSetsValue > 0
            ? Math.floor(targetSetsValue)
            : 1;
        const exerciseId = (ex as any)?.exerciseId ?? ex?.id ?? null;
        const isDropset =
          typeof exerciseId === 'string' && dropsetExerciseIds.has(exerciseId);
        const isDropsetSet = isDropset && targetSetsCount > 1 && roundIdx + 1 === targetSetsCount;
        const repValue = resolveRepForSet(repOverrides[globalIdx], repsPerSet, roundIdx);
        const repDisplay =
          isDropsetSet && typeof repValue !== 'number'
            ? 'X'
            : typeof repValue === 'number'
              ? String(repValue)
              : '';
        const isShuttleSprint = isShuttleSprintExerciseLabel(name);
        const weightVal = progress[globalIdx]?.sets?.[roundIdx]?.weight ?? null;
        const plannedWeights =
          Array.isArray((ex as any)?.plannedWeights)
            ? (ex as any).plannedWeights
            : Array.isArray((ex as any)?.weights)
              ? (ex as any).weights
              : [];
        const shuttleUsesRepsDistance =
          isShuttleSprint &&
          (hasPositiveNumber(plannedWeights) ||
            (typeof weightVal === 'number' && weightVal > 0));
        const repUnit = getRepUnit(name, {
          shuttleUsesRepsDistance,
        });
        const repText = repDisplay ? `${repDisplay} ${repUnit}` : '';
        const weightText =
          typeof weightVal === 'number' && weightVal > 0 ? `@ ${weightVal} kg` : '';
        const distanceText =
          typeof weightVal === 'number' && weightVal > 0 ? `${weightVal} m` : '';
        const detail = isShuttleSprint && shuttleUsesRepsDistance
          ? repText && distanceText
            ? `${repText} x ${distanceText}`
            : repText || distanceText
          : [repText, weightText].filter(Boolean).join(' ');
        return { globalIdx, name, detail };
      })
      .filter(Boolean) as Array<{ globalIdx: number; name: string; detail: string }>;
  }, [
    useRoundList,
    currentRoundIndex,
    blockExerciseIndices,
    exList,
    repOverrides,
    progress,
    dropsetExerciseIds,
  ]);

  const [repInput, setRepInput] = React.useState<number | ''>(
    typeof currentRepValue === 'number' ? currentRepValue : ''
  );
  React.useEffect(() => {
    setRepInput(typeof currentRepValue === 'number' ? currentRepValue : '');
  }, [currentRepValue]);

  const skipWeightInputSyncRef = React.useRef(false);
  const [weightInput, setWeightInput] = React.useState<number | ''>(activeWeight);
  React.useEffect(() => {
    if (skipWeightInputSyncRef.current) {
      skipWeightInputSyncRef.current = false;
      return;
    }
    setWeightInput(activeWeight);
  }, [activeWeight]);

  const [allExercises, setAllExercises] = React.useState<any[]>([]);
  React.useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/exercises`);
        const data = await res.json().catch(() => []);
        setAllExercises(Array.isArray(data) ? data : []);
      } catch {
        setAllExercises([]);
      }
    })();
  }, []);

  const exerciseById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const ex of allExercises) {
      if (ex?.id) map.set(ex.id, ex);
    }
    return map;
  }, [allExercises]);

  // The kit the next method needs, read off the catalog rather than the workout.
  // `buildStartDto` hands this card id/name/sets/reps/weights and nothing else,
  // so the equipment tags only exist on the catalog row — the same detour
  // `isBarbellExercise` below takes. Until that fetch lands the map is empty and
  // the list simply stays hidden, which is why this is its own memo: it has to
  // re-run when the catalog arrives, and the rest of the preview must not.
  const pendingEquipment = React.useMemo(() => {
    if (!pendingPreview) return [] as string[];
    return collectBlockEquipment(
      pendingPreview.exerciseIds.map((id) => (id ? exerciseById.get(id) : null))
    );
  }, [pendingPreview, exerciseById]);

  const isBarbellExercise = React.useCallback(
    (exerciseId?: string | null, fallbackName?: string | null) => {
      const ex = exerciseId ? exerciseById.get(exerciseId) : null;
      const equipment = Array.isArray(ex?.equipment) ? ex.equipment : [];
      if (hasBarbellEquipment(equipment)) return true;
      const name = fallbackName ?? ex?.nameRaw ?? ex?.raw ?? '';
      return isLikelyBarbellExerciseName(String(name));
    },
    [exerciseById]
  );

  const plateStrategy: PlateStrategy = 'paired';
  const currentExerciseName = current?.nameRaw ?? (current as any)?.raw ?? null;
  const currentWeightValue =
    typeof weightInput === 'number' && !Number.isNaN(weightInput) ? weightInput : null;
  const currentPlateStack =
    isBarbellExercise(currentExerciseId, currentExerciseName) && typeof currentWeightValue === 'number'
      ? resolveBarbellStackView({
          totalKg: currentWeightValue,
          strategy: plateStrategy,
          rxLoadLabel: currentWodRxLoad,
        })
      : null;
  // Reserve extra height when the plate-stack diagram is shown, so the active and
  // completed set states stay the same height (and the action button doesn't shift).
  const setBodyMinHClass =
    !isCurrentShuttleSprintExercise && currentPlateStack ? 'min-h-[304px]' : 'min-h-[272px]';

  const [editTarget, setEditTarget] = React.useState<{
    globalIdx: number;
    setIndex: number;
  } | null>(null);
  const [editRepInput, setEditRepInput] = React.useState<number | ''>('');
  const [editWeightInput, setEditWeightInput] = React.useState<number | ''>('');

  const openSetEditor = React.useCallback(
    (globalIdx: number, setIndex: number) => {
      const repVal = repOverrides[globalIdx]?.[setIndex] ?? null;
      const weightVal = progress[globalIdx]?.sets?.[setIndex]?.weight ?? null;
      setEditTarget({ globalIdx, setIndex });
      setEditRepInput(typeof repVal === 'number' ? repVal : '');
      setEditWeightInput(typeof weightVal === 'number' ? weightVal : '');
    },
    [progress, repOverrides]
  );

  const saveEditedSet = React.useCallback(() => {
    if (!editTarget) return;
    const { globalIdx, setIndex } = editTarget;
    const repValue =
      typeof editRepInput === 'number' && Number.isFinite(editRepInput)
        ? Math.max(1, Math.floor(editRepInput))
        : null;
    const weightValue =
      typeof editWeightInput === 'number' && !Number.isNaN(editWeightInput)
        ? editWeightInput
        : null;

    setRepOverrides((prev) => {
      const copy = prev.map((arr) => arr.slice());
      if (!copy[globalIdx]) copy[globalIdx] = [];
      while (copy[globalIdx].length <= setIndex) copy[globalIdx].push(null);
      copy[globalIdx][setIndex] = repValue;
      return copy;
    });

    setProgress((prev) => {
      const copy = [...prev];
      const exP = { ...copy[globalIdx] };
      const sets = Array.isArray(exP.sets) ? exP.sets.map((s) => ({ ...s })) : [];
      while (sets.length <= setIndex) sets.push({ weight: null, timeMs: null });
      sets[setIndex] = { ...sets[setIndex], weight: weightValue };
      exP.sets = sets;
      copy[globalIdx] = exP;
      return copy;
    });

    setEditTarget(null);
    showToast(`${roundLabel} updated`);
  }, [editRepInput, editTarget, editWeightInput, roundLabel]);

  const handleEditRepInputChange = React.useCallback((raw: string) => {
    if (raw === '' || raw == null) {
      setEditRepInput('');
      return;
    }
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    setEditRepInput(v);
  }, []);

  const adjustEditRep = React.useCallback((delta: number) => {
    const base = typeof editRepInput === 'number' ? editRepInput : null;
    if (base == null) {
      if (delta > 0) setEditRepInput(1);
      return;
    }
    const next = base + delta;
    if (next < 1) {
      setEditRepInput('');
      return;
    }
    setEditRepInput(next);
  }, [editRepInput]);

  const editSetsByExercise = blockExerciseIndices
    .map((globalIdx) => {
      const ex = exList[globalIdx];
      const sets = progress[globalIdx]?.sets ?? [];
      const doneSets = sets
        .map((rec, sIdx) =>
          isAmrap || rec?.timeMs != null ? { setIndex: sIdx, rec } : null
        )
        .filter(Boolean) as Array<{ setIndex: number; rec: SetRecord }>;
      if (!doneSets.length) return null;
      const name = ex?.nameRaw ?? (ex as any)?.raw ?? `Exercise ${globalIdx + 1}`;
      return { globalIdx, name, sets: doneSets };
    })
    .filter(Boolean) as Array<{
    globalIdx: number;
    name: string;
    sets: Array<{ setIndex: number; rec: SetRecord }>;
  }>;

  const editExercise = editTarget ? exList[editTarget.globalIdx] || null : null;
  const editSetNo = editTarget ? editTarget.setIndex + 1 : 1;
  const editTargetSetsRaw =
    editExercise?.targetSets ??
    (Array.isArray(editExercise?.repsPerSet) ? editExercise?.repsPerSet.length : 1);
  const editTargetSetsValue = Number(editTargetSetsRaw);
  const editTargetSetsCount =
    Number.isFinite(editTargetSetsValue) && editTargetSetsValue > 0
      ? Math.floor(editTargetSetsValue)
      : 1;
  const editExerciseId =
    (editExercise as any)?.exerciseId ?? editExercise?.id ?? null;
  const editIsDropsetExercise =
    typeof editExerciseId === 'string' && dropsetExerciseIds.has(editExerciseId);
  const editIsDropsetSet =
    editIsDropsetExercise &&
    editTargetSetsCount > 1 &&
    editSetNo === editTargetSetsCount;
  const editExerciseName = editExercise?.nameRaw ?? (editExercise as any)?.raw ?? null;
  const editIsShuttleSprintExercise = isShuttleSprintExerciseLabel(editExerciseName);
  const editHasPlannedDistance =
    hasPositiveNumber((editExercise as any)?.plannedWeights) ||
    hasPositiveNumber((editExercise as any)?.weights) ||
    (editTarget != null &&
      Array.isArray(progress[editTarget.globalIdx]?.sets) &&
      progress[editTarget.globalIdx]!.sets.some(
        (setRow) => typeof setRow?.weight === 'number' && setRow.weight > 0
      ));
  const editShuttleUsesRepsDistance =
    editIsShuttleSprintExercise && editHasPlannedDistance;
  const editIsDistanceExercise =
    isDistanceExerciseLabel(editExerciseName) &&
    (!editIsShuttleSprintExercise || !editShuttleUsesRepsDistance);
  const editRepLabel = isHoldExerciseLabel(editExerciseName)
    ? 'Hold (sec)'
    : editIsDistanceExercise
      ? 'Distance (m)'
      : 'Reps';
  const editRepPlaceholder = editIsDropsetSet ? 'X' : editRepLabel;
  const editSecondaryMetricLabel =
    editIsShuttleSprintExercise && editShuttleUsesRepsDistance
      ? t('Distance (m)', 'Distance (m)')
      : t('Weight (kg)', 'Weight (kg)');
  const editSecondaryMetricUnit =
    editIsShuttleSprintExercise && editShuttleUsesRepsDistance ? 'm' : 'kg';
  const editWeightValue =
    typeof editWeightInput === 'number' && !Number.isNaN(editWeightInput) ? editWeightInput : null;
  const editWodRxLoad = getWodRxForExercise((currentBlock as any)?.methodMeta, editExerciseId);
  const editPlateStack =
    isBarbellExercise(editExerciseId, editExerciseName) && typeof editWeightValue === 'number'
      ? resolveBarbellStackView({
          totalKg: editWeightValue,
          strategy: plateStrategy,
          rxLoadLabel: editWodRxLoad,
        })
      : null;

  const editSetsSection = editSetsByExercise.length || editTarget ? (
      <div className="mt-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800">
      <div className="text-xs opacity-70 mb-2">Edit {roundLabel.toLowerCase()}s</div>
      {editSetsByExercise.length ? (
        <div className="grid gap-3">
          {editSetsByExercise.map((ex) => (
            <div key={ex.globalIdx}>
              <div className="text-sm font-medium">{ex.name}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {ex.sets.map((s) => {
                  const labelParts = [`${roundLabel} ${s.setIndex + 1}`];
                  const secondaryUnit = isShuttleSprintExerciseLabel(ex.name) ? 'm' : 'kg';
                  if (typeof s.rec?.weight === 'number') labelParts.push(`${s.rec.weight} ${secondaryUnit}`);
                  if (typeof s.rec?.timeMs === 'number') labelParts.push(fmtTime(s.rec.timeMs));
                  const isEditing =
                    editTarget?.globalIdx === ex.globalIdx &&
                    editTarget?.setIndex === s.setIndex;
                  return (
                    <button
                      key={`${ex.globalIdx}-${s.setIndex}`}
                      type="button"
                      onClick={() => openSetEditor(ex.globalIdx, s.setIndex)}
                      className={[
                        'px-2.5 py-1.5 rounded-lg border text-xs',
                        isEditing
                          ? 'border-emerald-400 bg-emerald-50/70 dark:border-emerald-700 dark:bg-emerald-900/20'
                          : 'border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-900/40',
                      ].join(' ')}
                    >
                      {labelParts.join(' • ')}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs opacity-70">No {roundLabel.toLowerCase()}s saved yet.</div>
      )}

      {editTarget && (
        <div className="mt-3 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 p-3">
          <div className="text-xs uppercase tracking-wide opacity-70">Edit {roundLabel.toLowerCase()}</div>
          <div className="mt-1 font-medium">
            {(editExercise?.nameRaw ?? (editExercise as any)?.raw ?? 'Exercise')} • {roundLabel} {editSetNo}
          </div>

          <div className="mt-3 grid grid-cols-[1fr,1fr] gap-3 items-end">
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-white/80 dark:bg-gray-950/40 px-3 py-2 text-center">
              <div className="text-[10px] uppercase tracking-wider opacity-60">{editRepLabel}</div>
              <div className="mt-1 flex items-center justify-center gap-2">
                <button
                  type="button"
                  className="h-8 w-8 rounded-full border border-emerald-200 dark:border-emerald-800 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                  onClick={() => adjustEditRep(-1)}
                  disabled={controlsDisabled}
                  aria-label="Decrease reps"
                >
                  -
                </button>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  className="w-16 text-center text-2xl font-semibold tabular-nums bg-transparent outline-none appearance-none"
                  value={editRepInput}
                  onChange={(e) => handleEditRepInputChange(e.target.value)}
                  placeholder={editRepPlaceholder}
                  aria-label={`${editRepLabel} for edited ${roundLabel.toLowerCase()}`}
                  disabled={controlsDisabled}
                />
                <button
                  type="button"
                  className="h-8 w-8 rounded-full border border-emerald-200 dark:border-emerald-800 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                  onClick={() => adjustEditRep(1)}
                  disabled={controlsDisabled}
                  aria-label="Increase reps"
                >
                  +
                </button>
              </div>
            </div>

            <label className="text-sm">
              <div className="text-xs opacity-70 mb-1">{editSecondaryMetricLabel}</div>
              <input
                type="number"
                inputMode={editIsShuttleSprintExercise ? 'numeric' : 'decimal'}
                step={editIsShuttleSprintExercise ? 1 : 0.5}
                min={editIsShuttleSprintExercise ? 1 : 0}
                className="input w-full mb-1"
                value={editWeightInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setEditWeightInput(v === '' ? '' : Number(v));
                }}
                placeholder={editIsShuttleSprintExercise ? 'e.g. 15' : 'e.g. 50'}
                aria-label={`${editSecondaryMetricLabel} (${editSecondaryMetricUnit}) for edited ${roundLabel.toLowerCase()}`}
                disabled={controlsDisabled}
              />
              {editPlateStack && (
                <div className="mt-1">
                  <BarbellStack
                    totalKg={editWeightValue!}
                    strategy={plateStrategy}
                    rxLoadLabel={editWodRxLoad}
                  />
                </div>
              )}
            </label>
          </div>

          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={saveEditedSet} disabled={controlsDisabled}>
              Save changes
            </Button>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const updateRepForCurrentSet = React.useCallback(
    (next: number | null) => {
      if (currentGlobalIdx == null) return;
      const normalized =
        typeof next === 'number' && Number.isFinite(next)
          ? Math.max(1, Math.floor(next))
          : null;
      setRepOverrides((prev) => {
        const copy = prev.map((arr) => arr.slice());
        if (!copy[currentGlobalIdx]) copy[currentGlobalIdx] = [];
        copy[currentGlobalIdx][setNo - 1] = normalized;
        return copy;
      });
      setRepInput(normalized ?? '');
    },
    [currentGlobalIdx, setNo]
  );

  const handleRepInputChange = React.useCallback(
    (raw: string) => {
      if (raw === '' || raw == null) {
        updateRepForCurrentSet(null);
        return;
      }
      const v = Number(raw);
      if (!Number.isFinite(v)) return;
      updateRepForCurrentSet(v);
    },
    [updateRepForCurrentSet]
  );

  const adjustRep = React.useCallback(
    (delta: number) => {
      const base =
        typeof repInput === 'number'
          ? repInput
          : typeof currentRepValue === 'number'
            ? currentRepValue
            : null;
      if (typeof base !== 'number') {
        if (delta > 0) updateRepForCurrentSet(1);
        return;
      }
      const next = base + delta;
      if (next < 1) {
        updateRepForCurrentSet(isCurrentDropsetSet ? null : 1);
        return;
      }
      updateRepForCurrentSet(next);
    },
    [currentRepValue, isCurrentDropsetSet, repInput, updateRepForCurrentSet]
  );

  // The rep box is a fixed width, so the value steps down a size instead of
  // clipping: reps are 1–2 digits, but Distance (m) and Hold (sec) reach 3–4.
  // Empty falls back to something small because the placeholder is the label
  // ("Hold (sec)"), which is far wider than any number.
  const repFontClass =
    repInput === ''
      ? 'text-2xl'
      : String(repInput).length >= 4
        ? 'text-6xl'
        : String(repInput).length === 3
          ? 'text-7xl'
          : 'text-8xl';

  // ---------- Timer helpers (Workout) ----------
  const stopWorkoutTimer = React.useCallback(() => {
    if (workoutRef.current != null) {
      window.clearInterval(workoutRef.current);
      workoutRef.current = null;
    }
    workoutStartRef.current = null;
  }, []);

  const startWorkoutTimer = React.useCallback(() => {
    if (workoutRef.current != null) return;
    workoutStartRef.current = Date.now();
    workoutRef.current = window.setInterval(() => {
      if (workoutStartRef.current == null) return;
      const now = Date.now();
      setWorkoutMs(workoutBaseRef.current + (now - workoutStartRef.current));
    }, 100);
  }, []);

  const resetWorkoutTimer = React.useCallback(() => {
    stopWorkoutTimer();
    workoutBaseRef.current = 0;
    setWorkoutMs(0);
  }, [stopWorkoutTimer]);

  // ---------- Timer helpers (Exercise/Set) ----------
  const stopExerciseTimer = React.useCallback(() => {
    if (exerciseRef.current != null) {
      window.clearInterval(exerciseRef.current);
      exerciseRef.current = null;
    }
    exerciseStartRef.current = null;
  }, []);

  const startExerciseTimer = React.useCallback(() => {
    if (exerciseRef.current != null) return;
    exerciseStartRef.current = Date.now();
    exerciseRef.current = window.setInterval(() => {
      if (exerciseStartRef.current == null) return;
      const now = Date.now();
      setExerciseMs(exerciseBaseRef.current + (now - exerciseStartRef.current));
    }, 100);
  }, []);

  const resetExerciseTimer = React.useCallback((autostart: boolean) => {
    stopExerciseTimer();
    exerciseBaseRef.current = 0;
    setExerciseMs(0);
    if (autostart) startExerciseTimer();
  }, [stopExerciseTimer, startExerciseTimer]);

  const countdownTimerRef = React.useRef<number | null>(null);
  const countdownDoneRef = React.useRef<(() => void) | null>(null);
  const workoutBootedRef = React.useRef(false);

  const startCountdown = React.useCallback(
    (seconds: number, onComplete: () => void, kind: 'start' | 'rest' | 'method' = 'start') => {
    if (countdownTimerRef.current != null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    countdownDoneRef.current = onComplete;
    setCountdownKind(kind);
    setCountdown(seconds);
    countdownTimerRef.current = window.setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          if (countdownTimerRef.current) {
            window.clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          const done = countdownDoneRef.current;
          countdownDoneRef.current = null;
          setCountdownKind(null);
          if (kind === 'rest') cueEnd();
          else cueGo();
          if (done) done();
          return 0;
        }
        const next = c - 1;
        if (next <= 3) cueTick();
        return next;
      });
    }, 1000);
  }, []);

  // ---------- 5s Countdown - start both timers ----------
  React.useEffect(() => {
    // reset everything on open
    resetWorkoutTimer();
    resetExerciseTimer(false);
    pauseSw.reset();
    pauseTotalSw.reset();
    workoutStartedAtRef.current = null;
    methodStartOffsetRef.current = 0;
    setBlockIdx(0);
    setPendingNextMethodIdx(null);
      setAmrapRounds(0);
      setAmrapRoundTimes([]);
      amrapNotifiedRef.current = false;
      amrapResultsRef.current = [];
      amrapRoundTimesRef.current = [];
      lastRoundMarkRef.current = {};
    setPaused(false);
    setStepIdx(0);
    setCountdown(0);
    setCountdownKind(null);
    setEditTarget(null);
    setEditRepInput('');
    setEditWeightInput('');
    workoutBootedRef.current = false;
    const globalStartMs = requiresGlobalStart ? isoToMs(globalStartedAt) : null;
    const globalStartAnchorMs =
      typeof globalStartMs === 'number'
        ? globalStartMs + GLOBAL_START_COUNTDOWN_MS
        : null;

    if (resumeState) {
      const maxBlockIdx = Math.max(0, methodBlocks.length - 1);
      const safeBlockIdx = Math.min(
        Math.max(0, Number(resumeState.blockIdx) || 0),
        maxBlockIdx
      );
      const pendingIdx =
        typeof resumeState.pendingNextMethodIdx === 'number' &&
        resumeState.pendingNextMethodIdx >= 0 &&
        resumeState.pendingNextMethodIdx <= maxBlockIdx
          ? resumeState.pendingNextMethodIdx
          : null;
      const step =
        typeof resumeState.stepIdx === 'number' && resumeState.stepIdx >= 0
          ? resumeState.stepIdx
          : 0;
      const workoutResumeMs =
        typeof resumeState.workoutMs === 'number' && resumeState.workoutMs >= 0
          ? resumeState.workoutMs
          : 0;
      const exerciseResumeMs =
        typeof resumeState.exerciseMs === 'number' && resumeState.exerciseMs >= 0
          ? resumeState.exerciseMs
          : 0;
      const syncedWorkoutMs =
        typeof globalStartAnchorMs === 'number'
          ? Math.max(0, serverNow() - globalStartAnchorMs)
          : workoutResumeMs;
      const resumePaused =
        typeof globalStartAnchorMs === 'number'
          ? false
          : !!resumeState.paused;

      setBlockIdx(safeBlockIdx);
      setPendingNextMethodIdx(pendingIdx);
      setStepIdx(step);
      setPaused(resumePaused);
      setWorkoutMs(syncedWorkoutMs);
      setExerciseMs(exerciseResumeMs);
      setAmrapRounds(
        typeof resumeState.amrapRounds === 'number' && resumeState.amrapRounds >= 0
          ? resumeState.amrapRounds
          : 0
      );
      amrapNotifiedRef.current = false;
      amrapResultsRef.current = Array.isArray(resumeState.amrapResults)
        ? resumeState.amrapResults
        : [];
      if (Array.isArray(resumeState.amrapRoundTimes)) {
        amrapRoundTimesRef.current = resumeState.amrapRoundTimes
          .filter((x: any) => x && typeof x.blockId === 'string' && Array.isArray(x.roundTimesMs))
          .map((x: any) => ({
            blockId: String(x.blockId),
            roundTimesMs: x.roundTimesMs.filter((v: any) => Number.isFinite(v)),
          }));
      } else {
        amrapRoundTimesRef.current = [];
      }
      const resumeBlockId = String((methodBlocks[safeBlockIdx]?.id) ?? `method-${safeBlockIdx + 1}`);
      const resumeTimes =
        amrapRoundTimesRef.current.find((r) => r.blockId === resumeBlockId)?.roundTimesMs ?? [];
      setAmrapRoundTimes(resumeTimes);
      if (resumeTimes.length > 0) {
        setAmrapRounds(resumeTimes.length);
      }
      if (resumeTimes.length > 0) {
        lastRoundMarkRef.current[resumeBlockId] = sumMs(resumeTimes);
      }
      methodStartOffsetRef.current =
        typeof resumeState.methodStartOffsetMs === 'number'
          ? resumeState.methodStartOffsetMs
          : 0;
      workoutBaseRef.current = syncedWorkoutMs;
      workoutMsRef.current = syncedWorkoutMs;
      exerciseBaseRef.current = exerciseResumeMs;
      workoutStartedAtRef.current =
        typeof globalStartAnchorMs === 'number'
          ? globalStartAnchorMs
          : typeof resumeState.workoutStartedAtMs === 'number'
          ? resumeState.workoutStartedAtMs
          : Date.now() - workoutResumeMs;
      // The total is restored even in the coach-led branch that throws the
      // current set's pause away: re-syncing to the server's start anchor
      // invalidates the live clocks, not the pause the athlete already took.
      // An older snapshot has no `pauseTotalMs`, so it seeds from
      // `pauseElapsedMs` — that field *was* the de-facto total back when one
      // stopwatch did both jobs, which makes it the closest honest value.
      pauseTotalSw.setElapsed(
        typeof resumeState.pauseTotalMs === 'number'
          ? resumeState.pauseTotalMs
          : typeof resumeState.pauseElapsedMs === 'number'
            ? resumeState.pauseElapsedMs
            : 0
      );
      if (typeof globalStartAnchorMs === 'number') {
        pauseSw.stop();
        pauseSw.reset();
      } else if (typeof resumeState.pauseElapsedMs === 'number') {
        pauseSw.setElapsed(resumeState.pauseElapsedMs);
      }
      if (resumePaused) {
        pauseSw.start();
        pauseTotalSw.start();
      } else {
        pauseSw.stop();
        pauseTotalSw.stop();
      }
      skipWeightInputSyncRef.current = true;
      setWeightInput(
        typeof resumeState.weightInput === 'number' &&
          !Number.isNaN(resumeState.weightInput)
          ? resumeState.weightInput
          : ''
      );

      const resumeBlock = methodBlocks[safeBlockIdx] || null;
      const resumeTiming = getBlockTiming(resumeBlock);
      const resumeDurationMin = resumeTiming.durationMin;
      const resumeMode = resumeTiming.mode;
      const resumeIsEmom = resumeMode === 'emom';
      const resumeIsAmrap =
        resumeMode === 'amrap' ||
        (!resumeIsEmom && Number.isFinite(resumeDurationMin) && resumeDurationMin > 0);

      if (typeof globalStartAnchorMs === 'number' || !requiresGlobalStart) {
        const remainingCountdownMs =
          typeof globalStartAnchorMs === 'number'
            ? globalStartAnchorMs - serverNow()
            : 0;
        if (remainingCountdownMs > 0) {
          const countdownSec = Math.max(1, Math.ceil(remainingCountdownMs / 1000));
          workoutBootedRef.current = true;
          setWorkoutMs(0);
          workoutBaseRef.current = 0;
          workoutMsRef.current = 0;
          startCountdown(countdownSec, () => {
            workoutStartedAtRef.current = globalStartAnchorMs ?? Date.now();
            startWorkoutTimer();
            if (!resumeIsAmrap && !resumePaused && pendingIdx == null) {
              startExerciseTimer();
            }
          }, 'start');
        } else {
          startWorkoutTimer();
          workoutBootedRef.current = true;
          if (!resumeIsAmrap && !resumePaused && pendingIdx == null) {
            startExerciseTimer();
          }
        }
      }
    } else {
      const firstBlock = methodBlocks[0];
      const firstTiming = getBlockTiming(firstBlock);
      const firstDurationMin = firstTiming.durationMin;
      const firstMode = firstTiming.mode;
      const firstIsEmom = firstMode === 'emom';
      const firstIsAmrap =
        firstMode === 'amrap' ||
        (!firstIsEmom && Number.isFinite(firstDurationMin) && firstDurationMin > 0);

      if (typeof globalStartAnchorMs === 'number') {
        const remainingCountdownMs = globalStartAnchorMs - serverNow();
        if (remainingCountdownMs > 0) {
          const countdownSec = Math.max(1, Math.ceil(remainingCountdownMs / 1000));
          workoutBootedRef.current = true;
          workoutStartedAtRef.current = globalStartAnchorMs;
          workoutBaseRef.current = 0;
          workoutMsRef.current = 0;
          setWorkoutMs(0);
          startCountdown(countdownSec, () => {
            workoutStartedAtRef.current = globalStartAnchorMs;
            startWorkoutTimer();
            if (!firstIsAmrap) startExerciseTimer();
          }, 'start');
        } else {
          const globalElapsedMs = Math.max(0, serverNow() - globalStartAnchorMs);
          workoutStartedAtRef.current = globalStartAnchorMs;
          workoutBaseRef.current = globalElapsedMs;
          workoutMsRef.current = globalElapsedMs;
          setWorkoutMs(globalElapsedMs);
          startWorkoutTimer();
          workoutBootedRef.current = true;
          if (!firstIsAmrap) startExerciseTimer();
        }
      } else if (!requiresGlobalStart) {
        workoutBootedRef.current = true;
        startCountdown(GLOBAL_START_COUNTDOWN_SEC, () => {
          workoutStartedAtRef.current = Date.now();
          startWorkoutTimer();
          if (!firstIsAmrap) startExerciseTimer();
        }, 'start');
      }
    }

    return () => {
      if (countdownTimerRef.current != null) {
        window.clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      countdownDoneRef.current = null;
      stopWorkoutTimer();
      stopExerciseTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!requiresGlobalStart || workoutBootedRef.current) return;
    const globalStartMs = isoToMs(globalStartedAt);
    if (typeof globalStartMs !== 'number') return;
    const globalStartAnchorMs = globalStartMs + GLOBAL_START_COUNTDOWN_MS;

    const activeBlock = methodBlocks[Math.min(Math.max(blockIdx, 0), methodBlocks.length - 1)] || null;
    const activeTiming = getBlockTiming(activeBlock);
    const activeDurationMin = activeTiming.durationMin;
    const activeMode = activeTiming.mode;
    const activeIsEmom = activeMode === 'emom';
    const activeIsAmrap =
      activeMode === 'amrap' ||
      (!activeIsEmom && Number.isFinite(activeDurationMin) && activeDurationMin > 0);
    const remainingCountdownMs = globalStartAnchorMs - serverNow();

    workoutStartedAtRef.current = globalStartAnchorMs;
    setPaused(false);
    pauseSw.stop();
    pauseSw.reset();
    // Only reachable while `workoutBootedRef` is still false, i.e. the coach has
    // just started and this athlete has not trained a second yet — so there is no
    // total to lose here, unlike in the resume branch above.
    pauseTotalSw.stop();
    pauseTotalSw.reset();

    if (remainingCountdownMs > 0) {
      const countdownSec = Math.max(1, Math.ceil(remainingCountdownMs / 1000));
      workoutBaseRef.current = 0;
      workoutMsRef.current = 0;
      setWorkoutMs(0);
      workoutBootedRef.current = true;
      startCountdown(countdownSec, () => {
        workoutStartedAtRef.current = globalStartAnchorMs;
        startWorkoutTimer();
        if (!activeIsAmrap && pendingNextMethodIdx == null) {
          startExerciseTimer();
        }
      }, 'start');
      return;
    }

    const globalElapsedMs = Math.max(0, serverNow() - globalStartAnchorMs);
    workoutBaseRef.current = globalElapsedMs;
    workoutMsRef.current = globalElapsedMs;
    setWorkoutMs(globalElapsedMs);
    startWorkoutTimer();
    workoutBootedRef.current = true;
    if (!activeIsAmrap && pendingNextMethodIdx == null) {
      startExerciseTimer();
    }
  }, [
    blockIdx,
    globalStartedAt,
    methodBlocks,
    pauseSw,
    pauseTotalSw,
    pendingNextMethodIdx,
    requiresGlobalStart,
    startExerciseTimer,
    startWorkoutTimer,
  ]);

  React.useEffect(() => {
    if (!isEmom || emomSteps.length === 0) {
      emomIntervalRef.current = null;
      return;
    }
    if (waitingForCoachStart || countdown > 0 || isTransitionPending) return;

    const intervalMs = emomIntervalMs > 0 ? emomIntervalMs : 60 * 1000;
    const idx = Math.floor(methodElapsedMs / intervalMs);
    if (!Number.isFinite(idx)) return;
    const clamped = Math.min(Math.max(idx, 0), emomSteps.length - 1);
    if (emomIntervalRef.current == null) {
      emomIntervalRef.current = clamped;
      resetExerciseTimer(!paused);
      return;
    }
    if (clamped !== emomIntervalRef.current) {
      emomIntervalRef.current = clamped;
      resetExerciseTimer(!paused);
      cueGo(); // new interval — the exercise changes now
    }
  }, [
    countdown,
    emomIntervalMs,
    emomSteps.length,
    isEmom,
    isTransitionPending,
    waitingForCoachStart,
    methodElapsedMs,
    paused,
    resetExerciseTimer,
  ]);

  // Tick on the final 3 seconds before each EMOM interval boundary.
  const emomLastTickSecRef = React.useRef(-1);
  React.useEffect(() => {
    if (!isEmom || emomSteps.length === 0 || waitingForCoachStart || countdown > 0 || isTransitionPending || paused) {
      emomLastTickSecRef.current = -1;
      return;
    }
    const intervalMs = emomIntervalMs > 0 ? emomIntervalMs : 60 * 1000;
    const secLeft = Math.ceil((intervalMs - (methodElapsedMs % intervalMs)) / 1000);
    if (secLeft !== emomLastTickSecRef.current) {
      emomLastTickSecRef.current = secLeft;
      if (secLeft >= 1 && secLeft <= 3) cueTick();
    }
  }, [
    isEmom,
    emomSteps.length,
    emomIntervalMs,
    methodElapsedMs,
    waitingForCoachStart,
    countdown,
    isTransitionPending,
    paused,
  ]);

  // ---------- Actions ----------
  // The two pause clocks move together everywhere. `start`/`stop` are no-ops when
  // the stopwatch is already in that state, so mirroring them is safe even on the
  // paths that reach here twice.
  const handlePause = () => {
    if (paused || pauseDisabled) return;
    setPaused(true);
    stopExerciseTimer();
    exerciseBaseRef.current = exerciseMs;
    pauseSw.start();
    pauseTotalSw.start();
  };

  const handleResume = () => {
    if (!paused || pauseDisabled) return;
    setPaused(false);
    pauseSw.stop();
    pauseTotalSw.stop();
    startExerciseTimer();
  };

  const handleResetExerciseTimer = () => {
    if (countdown > 0 || isTransitionPending) return;
    resetExerciseTimer(!paused);
  };

  // Save (weight + time) for current set, but DO NOT advance.
  const saveCurrentSet = () => {
    if (currentGlobalIdx == null) return;
    setProgress(prev => {
      const copy = [...prev];
      const exP = { ...copy[currentGlobalIdx] };
      const sets = Array.isArray(exP.sets) ? exP.sets.map(s => ({ ...s })) : [];
      while (sets.length < setNo) {
        sets.push({ weight: null, timeMs: null });
      }
      const w = (typeof weightInput === 'number' && !Number.isNaN(weightInput)) ? weightInput : null;
      sets[setNo - 1] = { weight: w, timeMs: exerciseMs, pauseMs: pauseSw.elapsed };
      exP.sets = sets;
      copy[currentGlobalIdx] = exP;
      return copy;
    });
    showToast(`${roundLabel} saved`);
  };

  // Write only the pause onto the current set, leaving weight and time alone.
  // `saveCurrentSet` already records it as part of a full save, but Sets & Reps is
  // a two-tap flow — "Set Done" saves, "Next" advances with `skipSave` — and the
  // set-complete screen in between is exactly where an athlete rests. That pause
  // belongs to the set they just finished, so it is read again at the advance.
  const recordPauseForCurrentSet = () => {
    if (currentGlobalIdx == null) return;
    const pauseMs = pauseSw.elapsed;
    setProgress(prev => {
      const copy = [...prev];
      const exP = { ...copy[currentGlobalIdx] };
      const sets = Array.isArray(exP.sets) ? exP.sets.map(s => ({ ...s })) : [];
      while (sets.length < setNo) {
        sets.push({ weight: null, timeMs: null });
      }
      sets[setNo - 1] = { ...sets[setNo - 1], pauseMs };
      exP.sets = sets;
      copy[currentGlobalIdx] = exP;
      return copy;
    });
  };

  const handleSetDone = () => {
    stopExerciseTimer();
    saveCurrentSet();
    setSetDoneConfirmed(true);
  };

  // Save & advance to next set / exercise
  const saveAndNext = async (opts?: { skipSave?: boolean }) => {
    if (currentGlobalIdx == null) return;
    if (!opts?.skipSave) saveCurrentSet();
    else recordPauseForCurrentSet();
    if (isEmom) {
      return;
    }
    if (nextStep) {
      const shouldRest =
        pauseMode === 'down' &&
        typeof pauseDurationSec === 'number' &&
        pauseDurationSec > 0;
      setStepIdx(s => s + 1);
      exerciseBaseRef.current = 0;
      setExerciseMs(0);
      stopExerciseTimer();
      // Zeroed on *every* advance now, not just on a change of exercise (which is
      // why the `isExerciseChange` flag that used to guard this is gone): the
      // right-hand readout is the pause for one set, and `saveCurrentSet` above
      // has already copied the outgoing value onto that set's record. `reset()`
      // rather than `stop()` + `reset()` because it keeps the stopwatch running if
      // it was running — tapping Next while still paused used to freeze the
      // display at 0:00 with `paused` stuck true. The total lives on
      // `pauseTotalSw` and is deliberately untouched here.
      pauseSw.reset();
      if (shouldRest) {
        startCountdown(
          pauseDurationSec,
          () => {
            if (!paused) startExerciseTimer();
          },
          'rest'
        );
        return;
      }
      if (!paused) startExerciseTimer();
      return;
    }

    // last step in method
    if (blockIdx < methodBlocks.length - 1) {
      queueNextMethod(blockIdx + 1);
    } else {
      await finishWorkout();
    }
  };

  const finishRound = async () => {
    if (!useRoundWorkflow || actionDisabled) return;
    const roundTime = recordRoundTime();
    if (typeof roundTime === 'number') {
      setProgress((prev) => {
        const copy = [...prev];
        for (const ex of blockExerciseDetails) {
          if (currentRoundIndex >= Math.max(ex.sets, roundCount || 0)) continue;
          const exP = { ...copy[ex.globalIdx] };
          const sets = Array.isArray(exP.sets) ? exP.sets.map((s) => ({ ...s })) : [];
          while (sets.length <= currentRoundIndex) sets.push({ weight: null, timeMs: null });
          const existing = sets[currentRoundIndex] ?? { weight: null, timeMs: null };
          // A round's pause belongs to the round, so it lands on every exercise in
          // it — the same shape `roundTime` already has here. Repeated, not split:
          // nothing sums these (the workout total is its own stopwatch), so a
          // round that was paused 40s reads "40s" on each of its rows.
          sets[currentRoundIndex] = { ...existing, timeMs: roundTime, pauseMs: pauseSw.elapsed };
          exP.sets = sets;
          copy[ex.globalIdx] = exP;
        }
        return copy;
      });
    }

    const nextIdx = roundStartIdx + roundStepCount;
    if (nextIdx >= blockSteps.length) {
      setRoundWorkflowDoneAtMs(Math.max(0, workoutMsRef.current - methodStartOffsetRef.current));
    }
    if (nextIdx < blockSteps.length) {
      const shouldRest =
        pauseMode === 'down' &&
        typeof pauseDurationSec === 'number' &&
        pauseDurationSec > 0;
      setStepIdx(nextIdx);
      exerciseBaseRef.current = 0;
      setExerciseMs(0);
      stopExerciseTimer();
      pauseSw.reset(); // new round = new per-set pause, same as an advance above
      if (shouldRest) {
        startCountdown(
          pauseDurationSec,
          () => {
            if (!paused) startExerciseTimer();
          },
          'rest'
        );
        return;
      }
      if (!paused) startExerciseTimer();
      return;
    }

    if (blockIdx < methodBlocks.length - 1) {
      queueNextMethod(blockIdx + 1);
    } else {
      await finishWorkout();
    }
  };

  const handleAbort = () => {
    const { resumeState: _resumeState, resumeMode: _resumeMode, ...trainingBase } =
      training as PlannedTrainingDTO;
    const plannedId = training.plannedId ?? training.id ?? null;
    const persistedTraining: PlannedTrainingDTO = {
      ...trainingBase,
      globalStartedAt: globalStartedAt ?? null,
      requiresGlobalStart,
      readyByMe,
    };
    const resumePayload: StartedTrainingResumeState = {
      blockIdx,
      stepIdx,
      pendingNextMethodIdx,
      workoutMs,
      workoutStartedAtMs:
        typeof workoutStartedAtRef.current === 'number'
          ? workoutStartedAtRef.current
          : Date.now() - workoutMs,
      exerciseMs,
      paused,
      progress,
      repOverrides,
      amrapRounds,
      amrapResults: Array.isArray(amrapResultsRef.current)
        ? amrapResultsRef.current
        : [],
      amrapRoundTimes: Array.isArray(amrapRoundTimesRef.current)
        ? amrapRoundTimesRef.current
        : [],
      methodStartOffsetMs: methodStartOffsetRef.current,
      pauseElapsedMs: pauseSw.elapsed,
      pauseTotalMs: pauseTotalSw.elapsed,
      weightInput:
        typeof weightInput === 'number' && !Number.isNaN(weightInput)
          ? weightInput
          : null,
    };
    const snapshot = {
      plannedId,
      savedAt: Date.now(),
      training: persistedTraining,
      resumeState: resumePayload,
    };
    writeStartedTrainingSnapshot(snapshot);
    void saveStartedTrainingSnapshotRemote(snapshot);
    stopTraining();
  };

  const recordAmrapResult = React.useCallback(() => {
    if (!amrapDurationMin || !currentBlock) return;
    const blockId = String(currentBlock?.id ?? `method-${blockIdx + 1}`);
    const existing = amrapResultsRef.current.find((r) => r.blockId === blockId);
    const payload = { blockId, durationMin: amrapDurationMin, rounds: amrapRounds };
    if (existing) {
      existing.durationMin = payload.durationMin;
      existing.rounds = payload.rounds;
    } else {
      amrapResultsRef.current.push(payload);
    }
  }, [amrapDurationMin, amrapRounds, currentBlock, blockIdx]);

  const queueNextMethod = React.useCallback((nextIndex: number) => {
    recordAmrapResult();
    const nextBlock = methodBlocks[nextIndex];
    if (!nextBlock) {
      void finishWorkout();
      return;
    }
    stopExerciseTimer();
    setPendingNextMethodIdx(nextIndex);
  }, [recordAmrapResult, methodBlocks, stopExerciseTimer]);

  const startNextMethod = React.useCallback((nextIndex: number) => {
    const nextBlock = methodBlocks[nextIndex];
    if (!nextBlock) {
      setPendingNextMethodIdx(null);
      void finishWorkout();
      return;
    }
    startCountdown(5, () => {
      methodStartOffsetRef.current = workoutMsRef.current;
      amrapNotifiedRef.current = false;
      setRoundWorkflowDoneAtMs(null);
      setAmrapRounds(0);
      setPendingNextMethodIdx(null);
      setBlockIdx(nextIndex);
      setStepIdx(0);
      exerciseBaseRef.current = 0;
      setExerciseMs(0);
      stopExerciseTimer();
      setPaused(false);
      pauseSw.stop();
      // A new method means a new set 1, so the per-set pause starts over — it used
      // to carry the previous block's last pause into the first set of the next
      // one. The total only stops here, matching `setPaused(false)` above; it
      // keeps its value across the whole workout by design.
      pauseSw.reset();
      pauseTotalSw.stop();
      const nextTiming = getBlockTiming(nextBlock);
      const nextDurationMin = nextTiming.durationMin;
      const nextMode = nextTiming.mode;
      const nextIsEmom = nextMode === 'emom';
      const nextIsAmrap =
        nextMode === 'amrap' ||
        (!nextIsEmom && Number.isFinite(nextDurationMin) && nextDurationMin > 0);
      if (!nextIsAmrap) startExerciseTimer();
    }, 'method');
  }, [methodBlocks, pauseSw, pauseTotalSw, startCountdown, startExerciseTimer, stopExerciseTimer]);

  React.useEffect(() => {
    if (!emomTargetMs) {
      emomFinishedRef.current = false;
      return;
    }
    if (methodElapsedMs < emomTargetMs) {
      emomFinishedRef.current = false;
      return;
    }
    if (emomFinishedRef.current) return;
    emomFinishedRef.current = true;
    showToast('EMOM time is up');
    cueEnd();
  }, [emomTargetMs, methodElapsedMs]);

  // UI helpers for collapsed summary rows
  const renderSetSummary = (sIdx: number) => {
    const rec = currentGlobalIdx != null ? progress[currentGlobalIdx]?.sets?.[sIdx] : null;
    const isDone = rec?.timeMs != null;
    const w = rec?.weight;
    const tm = rec?.timeMs;
    const isEditing = editTarget?.globalIdx === currentGlobalIdx && editTarget?.setIndex === sIdx;
    const isFuture = sIdx > (setNo - 1);
    return (
      <button
        type="button"
        onClick={() => { if (currentGlobalIdx == null) return; openSetEditor(currentGlobalIdx, sIdx); }}
        className={[
          'w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border transition',
          isDone ? 'border-emerald-300/60 dark:border-emerald-800/40 bg-emerald-50/40 dark:bg-emerald-900/10 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' :
          isEditing ? 'border-brand-300/60 dark:border-brand-700/40 bg-brand-50/40 dark:bg-brand-900/10' :
          isFuture ? 'border-gray-200/40 dark:border-white/5 opacity-50' :
          'border-gray-200/60 dark:border-white/10 hover:bg-gray-50/60 dark:hover:bg-white/5',
        ].join(' ')}
      >
        <div className={['h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold',
          isDone ? 'bg-emerald-500 text-white' : 'border border-gray-300 dark:border-gray-600 opacity-50 text-xs'
        ].join(' ')}>
          {isDone ? '✓' : sIdx + 1}
        </div>
        <div className="flex-1 text-sm font-medium text-left">
          {roundLabel} {sIdx + 1}
          {typeof w === 'number' ? <span className="ml-2 text-xs font-normal opacity-60">{w} {secondaryMetricUnit}</span> : null}
          {typeof tm === 'number' ? <span className="ml-2 text-xs font-normal opacity-60">{fmtTime(tm)}</span> : null}
        </div>
        <ChevronRight size={14} className="opacity-30 shrink-0" />
      </button>
    );
  };
  
async function finishWorkout() {
  try {
    recordAmrapResult();

    // Build a local progress snapshot that includes the *current* set with its latest time/weight
    const effectiveProgress = progress.map((exProg, exIdx) => {
      if (currentGlobalIdx == null || exIdx !== currentGlobalIdx) return exProg;

      const copy = {
        ...exProg,
        sets: exProg.sets.map((s) => ({ ...s })),
      };
      while (copy.sets.length < setNo) {
        copy.sets.push({ weight: null, timeMs: null });
      }

      const w =
        typeof weightInput === 'number' && !Number.isNaN(weightInput)
          ? weightInput
          : null;

      copy.sets[setNo - 1] = {
        weight: w,
        timeMs: exerciseMs, // 👈 last set timer
        pauseMs: pauseSw.elapsed, // 👈 and the pause taken during it
      };

      return copy;
    });

    // Optional: still update React state for UI; not used for saving anymore
    saveCurrentSet();

    // Build details payload from effectiveProgress
    const details: any = {
      exercises: (exList || []).map((ex, i) => {
        const prog = effectiveProgress[i];
        const sets = Array.isArray(prog?.sets) ? prog!.sets : [];
        const repsPerSet = Array.isArray(ex?.repsPerSet) ? ex.repsPerSet : null;
        const repTargets = repOverrides[i] ?? repsPerSet ?? null;

        return {
          exerciseId: ex?.exerciseId ?? ex?.id ?? `ex-${i}`,
          name: ex?.nameRaw ?? ex?.raw ?? null,
          order: i,
          targetSets: ex?.targetSets ?? (repsPerSet ? repsPerSet.length : null),
          repsPerSet: repTargets ?? null,
          sets: sets.map((s, idx) => ({
            // Resolve per round so EMOM/X-Rounds record the rep target for every
            // round, not just the first (repTargets is only seeded to targetSets).
            repTarget: resolveRepForSet(repOverrides[i], repsPerSet, idx),
            weight: typeof s?.weight === 'number' ? s.weight : null,
            timeMs: typeof s?.timeMs === 'number' ? s.timeMs : null,
            pauseMs: typeof s?.pauseMs === 'number' ? s.pauseMs : null,
          })),
        };
      }),
      timers: { workoutMs },
      // The whole workout, finally. This used to read `pauseSw.elapsed`, which was
      // reset on every exercise change, so it stored the last exercise's pause
      // under the name "total" for every workout ever saved.
      pause: { totalPauseMs: pauseTotalSw.elapsed },
      // 👇 carry plannerMeta so Completed can show methods even if planned=null
      plannerMeta: (training as any)?.plannerMeta ?? null,
    };

    if (amrapResultsRef.current.length > 0) {
      details.amrapBlocks = amrapResultsRef.current.map((b) => ({
        blockId: b.blockId,
        durationMin: b.durationMin,
        rounds: b.rounds,
        roundTimesMs:
          amrapRoundTimesRef.current.find((t) => t.blockId === b.blockId)?.roundTimesMs ??
          [],
      }));
      const last = amrapResultsRef.current[amrapResultsRef.current.length - 1];
      details.amrap = {
        durationMin: last.durationMin,
        rounds: last.rounds,
        roundTimesMs:
          amrapRoundTimesRef.current.find((t) => t.blockId === last.blockId)?.roundTimesMs ??
          [],
      };
    }

    const endedAt = new Date().toISOString();
    const startedAt =
      typeof workoutStartedAtRef.current === 'number'
        ? new Date(workoutStartedAtRef.current).toISOString()
        : typeof workoutStartRef.current === 'number'
          ? new Date(workoutStartRef.current).toISOString()
          : endedAt;

    const body = {
      plannedId:
        (training as any)?.plannedId ??
        (training as any)?.id ??
        null,
      startedAt,
      endedAt,
      methodId:
        training?.method?.id ?? (training as any)?.methodId ?? null,
      title: training?.title ?? null,
      tz:
        training?.tz ??
        Intl.DateTimeFormat().resolvedOptions().timeZone ??
        null,
      notes: training?.notes ?? null,
      details,
    };

    console.log('[finishWorkout] body', body);

    const res = await authFetchJSON(`${API_BASE}/api/completed`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      console.error('[finishWorkout] POST /api/completed failed:', res);
      showToast('Failed to save completed training', 'error');
      return;
    }

    showToast('Workout completed! 🎉', 'success');

    const prs = (res.data as any)?.prs;
    if (Array.isArray(prs) && prs.length) {
      window.dispatchEvent(new CustomEvent('workout:prs', { detail: prs }));
    }

    clearStartedTrainingSnapshot();
    void clearStartedTrainingSnapshotRemote();

    // Optional: remove from planned list on success
    try {
      const planId = (training as any)?.id;
      if (planId) {
        await authFetch(`${API_BASE}/api/trainings/${planId}`, {
          method: 'DELETE',
        });
      }
    } catch (e) {
      console.warn('Optional planned-delete failed:', e);
    }

    window.dispatchEvent(new CustomEvent('completed:refresh'));
    window.dispatchEvent(new CustomEvent('planned:refresh'));
    stopTraining();
  } catch (e) {
    console.error('[finishWorkout] error', e);
    showToast('Failed to save completed training', 'error');
  }
}


  const isDefaultView =
    !isTransitionPending && !amrapTargetMs && !useRoundWorkflow && !isComplexEmom;

  // Timer card — full width for AMRAP/round/transition, compact for the default right column.
  const renderTimerCard = () => (
    <div className="relative rounded-2xl border border-gray-200/60 dark:border-white/10 overflow-hidden">
      {/* Main timer */}
      <div className="relative px-4 pt-2 pb-3 text-center">
        {/* Sound on/off — bottom-right, next to the interval/summary label */}
        <button
          type="button"
          onClick={toggleSound}
          aria-label={soundMuted ? t('Unmute sounds', 'Unmute sounds') : t('Mute sounds', 'Mute sounds')}
          title={soundMuted ? t('Unmute sounds', 'Unmute sounds') : t('Mute sounds', 'Mute sounds')}
          className="absolute bottom-2 right-2 z-10 grid h-8 w-8 place-items-center rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
        >
          {soundMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        {isPartnerIgyg && (
          <div className="flex justify-center mb-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
              <Users size={11} /> {t('I Go You Go', 'I Go You Go')}
            </span>
          </div>
        )}
        {!isEmom && (
          <div className="text-[10px] font-semibold uppercase tracking-widest opacity-40 mb-1.5">
            {isAmrap ? t('AMRAP Countdown', 'AMRAP Countdown') : timeCapTargetMs ? t('Time Cap', 'Time Cap') : t('Workout Timer', 'Workout Timer')}
          </div>
        )}
        <div className={['text-6xl sm:text-7xl font-black tabular-nums tracking-tight leading-none', ((isEmom && emomTimeUp) || (isAmrap && amrapTimeUp) || timeCapTimeUp) ? 'text-rose-500' : ''].join(' ')}>
          {timeCapTimeUp && timeCapOvertimeMs > 0
            ? `+${fmtTime(timeCapOvertimeMs)}`
            : fmtTime(workoutDisplayMs)}
        </div>
        {(amrapTargetMs || emomTargetMs || timeCapTargetMs) && (
          <div className={['text-xs mt-2 font-medium', (isEmom && emomTimeUp) || (isAmrap && amrapTimeUp) || timeCapTimeUp ? 'text-rose-500' : 'opacity-40'].join(' ')}>
            {isEmom ? (emomTimeUp ? t('Time is up', 'Time is up') : `${emomSummaryLabel} · Interval ${emomIntervalLabel}`) : isAmrap ? (amrapTimeUp ? t('Time is up', 'Time is up') : `${amrapDurationMin} min AMRAP`) : timeCapTimeUp ? `Time cap ${fmtTime(timeCapSec! * 1000)}` : timeCapSec ? `Time cap ${fmtTime(timeCapSec * 1000)}` : 'Time cap'}
          </div>
        )}
      </div>
      {/* EMOM per-interval countdown (seconds left in this minute) */}
      {isEmom && !emomTimeUp && (
        <div className={['border-t flex items-center justify-center gap-2 px-4 py-2.5 transition-colors',
          emomIntervalRemainingMs <= 3000
            ? 'border-rose-200/60 dark:border-rose-800/40 bg-rose-50/60 dark:bg-rose-900/10'
            : 'border-gray-200/60 dark:border-white/10'].join(' ')}>
          <span className="text-[10px] font-semibold uppercase tracking-widest opacity-40">{t('Next up in', 'Next up in')}</span>
          <span className={['text-3xl font-black tabular-nums leading-none', emomIntervalRemainingMs <= 3000 ? 'text-rose-500' : 'text-brand-600 dark:text-brand-400'].join(' ')}>
            {fmtTime(emomIntervalRemainingMs)}
          </span>
        </div>
      )}
    </div>
  );

  // ---------- UI ----------
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex flex-col bg-white dark:bg-slate-950"
      style={{ paddingTop: 'var(--safe-top, 0px)', paddingBottom: 'var(--safe-bottom, 0px)' }}
    >
      {/* A throw in here used to unmount the whole React root — a blank white
          screen mid-workout with no way back. Keep the failure inside the overlay
          and show what went wrong. */}
      <ErrorBoundary
        title="Workout view error"
        onClose={stopTraining}
        closeLabel={t('Close', 'Close')}
      >
      {/* I Go You Go: host runs the normal workout; on an IGYG block this panel
          takes over so a friend can join the shared block (invite from here). */}
      {isPartnerIgyg && igygSelfUserId && (
        <IgygHostPanel
          training={training}
          blockId={currentBlockId}
          blockTitle={training.title ?? ''}
          exercises={igygExercises}
          isAmrap={isAmrap}
          durationMin={methodDurationMin ?? null}
          selfUserId={igygSelfUserId}
          selfName={igygSelfName}
          hasNextMethod={hasNextMethod}
          onAdvance={() => { if (hasNextMethod) { queueNextMethod(blockIdx + 1); } else { void finishWorkout(); } }}
          onAbort={handleAbort}
        />
      )}
      {/* Full-page panel */}
      <div className="relative w-full flex-1 min-h-0 overflow-hidden flex flex-col bg-white dark:bg-slate-950">

        {/* ── Header ── */}
        <div className="px-4 py-3 flex items-center gap-3 shrink-0 border-b border-gray-200/60 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.03]">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{training.title ?? t('Workout', 'Workout')}</div>
            {methodSubtitle && <div className="text-[11px] opacity-40 truncate">{methodSubtitle}</div>}
          </div>
          <button type="button" onClick={handleAbort} className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10 transition text-gray-500 dark:text-gray-400">
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        {/* Fits-or-scroll safety net: overflow-y-auto shows a scrollbar only when
            the content can't fit (small screens / longer workouts) so Save Set is
            always reachable; when it fits, there's no scroll. */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden relative">

          {/* Countdown overlay */}
          {countdown > 0 && countdownKind !== 'rest' && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/92 dark:bg-slate-950/92 backdrop-blur-sm">
              <div className="text-center">
                <div className="text-xs font-semibold uppercase tracking-widest opacity-50 mb-3">
                  {countdownKind === 'method' ? 'Next method starts in' : 'Starting in'}
                </div>
                <div className={['text-9xl font-black tabular-nums', restFinalSeconds ? 'text-rose-500' : 'text-brand-600 dark:text-brand-400'].join(' ')}>
                  {countdown}
                </div>
              </div>
            </div>
          )}

          {/* Waiting for coach overlay */}
          {waitingForCoachStart && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/92 dark:bg-slate-950/92 backdrop-blur-sm p-4">
              <div className="w-full max-w-sm rounded-2xl border border-gray-200/60 dark:border-white/10 bg-white dark:bg-slate-900 shadow-xl p-5 flex flex-col gap-3">
                <div className="text-base font-semibold">{t('Waiting for Coach', 'Waiting for Coach')}</div>
                <div className="text-sm opacity-60">{t('The Workout is not started. Please wait for the Coach', 'The Workout is not started. Please wait for the Coach')}</div>
                {readyByMe && <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{t('You are marked as ready', 'You are marked as ready')}</div>}
                {training.title && <div className="text-xs opacity-40 truncate">{training.title}</div>}
                {globalSyncError && <div className="text-xs text-red-500">{globalSyncError}</div>}
                <div className="flex items-center justify-end gap-2">
                  <Button variant={readyByMe ? 'success' : 'outline'} size="sm" onClick={handleMarkReady} loading={readySaving} disabled={readyByMe || readySaving || !plannedId}>
                    {t('Ready', 'Ready')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void loadJoinState()} loading={globalSyncLoading}>
                    <RefreshCcw size={13} /> {t('Refresh', 'Refresh')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 px-4 pb-4 pt-0 min-h-full">

            {/* ── Timer (full width for AMRAP / round / transition views) ── */}
            {!isDefaultView && renderTimerCard()}

            {/* ── Method transition ── */}
            {isTransitionPending && (
              <div className="rounded-2xl border border-amber-200/60 dark:border-amber-800/40 bg-amber-50/60 dark:bg-amber-900/10 p-5">
                <div className="text-center">
                  <div className="text-[10px] font-semibold uppercase tracking-widest opacity-50 mb-1">{t('Method Complete', 'Method Complete')}</div>
                  <div className="text-lg font-bold">{pendingLabel ? `${t('Next', 'Next')}: ${pendingLabel}` : t('Next method ready', 'Next method ready')}</div>
                </div>

                {pendingStructure.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                    {pendingStructure.map((chip) => (
                      <span key={chip} className="rounded-full border border-amber-300/50 dark:border-amber-700/40 bg-white/70 dark:bg-white/5 px-2.5 py-1 text-[11px] font-semibold tabular-nums">
                        {chip}
                      </span>
                    ))}
                  </div>
                )}

                {/* What's coming, so you can fetch the kit and set the loads
                    before the clock starts instead of after it. */}
                {pendingPreview && pendingPreview.exercises.length > 0 && (
                  <div className="mt-4 rounded-xl border border-amber-200/60 dark:border-amber-800/40 bg-white/60 dark:bg-white/5 overflow-hidden text-left">
                    <div className="px-3 py-2 border-b border-amber-200/60 dark:border-amber-800/40">
                      <span className="text-[10px] font-semibold uppercase tracking-widest opacity-50">{t('Exercises', 'Exercises')}</span>
                    </div>
                    <div className="divide-y divide-amber-200/50 dark:divide-amber-800/30">
                      {pendingPreview.exercises.map((ex) => (
                        <div key={ex.key} className="px-3 py-2 flex items-baseline justify-between gap-3">
                          <span className="text-sm font-medium">{ex.name}</span>
                          {ex.detail && <span className="text-xs opacity-60 tabular-nums shrink-0">{ex.detail}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pendingEquipment.length > 0 && (
                  <div className="mt-3 text-left">
                    <div className="text-[10px] font-semibold uppercase tracking-widest opacity-50 mb-1.5">{t('Equipment needed', 'Equipment needed')}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {pendingEquipment.map((token) => (
                        <span key={token} className="inline-flex items-center gap-1 rounded-full border border-amber-300/50 dark:border-amber-700/40 bg-white/70 dark:bg-white/5 px-2.5 py-1 text-[11px] font-medium">
                          <Package size={11} className="opacity-40 shrink-0" />
                          {equipmentLabel(token)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 text-center">
                  <Button variant="primary" onClick={() => { if (pendingNextMethodIdx != null) startNextMethod(pendingNextMethodIdx); }} disabled={countdown > 0 || pendingNextMethodIdx == null}>
                    {t('Start next Method', 'Start next Method')}
                  </Button>
                </div>
              </div>
            )}

            {/* ── AMRAP ── */}
            {!isTransitionPending && amrapTargetMs && (<>
              <div className="rounded-2xl border border-brand-300/60 dark:border-brand-700/40 bg-brand-50/50 dark:bg-brand-900/10 px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-600 dark:text-brand-400 opacity-70">{t('Rounds completed', 'Rounds completed')}</div>
                  <div className="text-6xl font-black tabular-nums mt-1">{amrapRounds}</div>
                </div>
                <Button variant="primary" onClick={() => { if (!amrapTargetMs) return; recordRoundTime(); }} disabled={actionDisabled}>
                  {t('+ Round', '+ Round')}
                </Button>
              </div>
              {roundTimesList}
              {amrapTimeUp && <div className="text-center text-xs text-rose-500 font-semibold">{t('AMRAP time is up.', 'AMRAP time is up.')}</div>}
              <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50/60 dark:bg-white/5 border-b border-gray-200/60 dark:border-white/10">
                  <span className="text-[11px] font-semibold uppercase tracking-widest opacity-50">{t('Exercises', 'Exercises')}</span>
                </div>
                {blockExercises.length === 0 ? <div className="px-4 py-4 text-sm opacity-50">{t('No exercises', 'No exercises')}</div> : (
                  <div className="divide-y divide-gray-200/60 dark:divide-white/10">
                    {blockExercises.map((ex, exIdx) => {
                      const name = ex?.nameRaw ?? (ex as any)?.raw ?? `Exercise ${exIdx + 1}`;
                      const reps = Array.isArray((ex as any)?.repsPerSet) ? (ex as any).repsPerSet.filter((r: any) => Number.isFinite(r)) : [];
                      const repText = reps.length ? reps.join('/') : '';
                      const isShuttleSprint = isShuttleSprintExerciseLabel(name);
                      const weightsRaw = Array.isArray((ex as any)?.plannedWeights) ? (ex as any).plannedWeights.filter((w: any) => Number.isFinite(w)) : [];
                      const hasWeights = weightsRaw.some((w: number) => w > 0);
                      const repUnit = getRepUnit(name, { shuttleUsesRepsDistance: isShuttleSprint && hasWeights });
                      const weightText = hasWeights ? weightsRaw.join('/') : '';
                      const parts: string[] = [];
                      if (typeof (ex as any)?.targetSets === 'number' && (ex as any).targetSets > 0) { const sc = Number((ex as any).targetSets); parts.push(`${sc} set${sc === 1 ? '' : 's'}`); }
                      if (repText) parts.push(`${repText} ${repUnit}`);
                      if (weightText) parts.push(`${weightText} ${isShuttleSprint && hasWeights ? 'm' : 'kg'}`);
                      return (
                        <div key={`${exIdx}-${name}`} className="px-4 py-3">
                          <div className="text-sm font-medium">{name}</div>
                          {parts.length > 0 && <div className="text-xs opacity-50 mt-0.5">{parts.join(' · ')}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>)}

            {/* ── Round workflow ── */}
            {!isTransitionPending && !amrapTargetMs && useRoundWorkflow && (<>
              <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50/60 dark:bg-white/5 border-b border-gray-200/60 dark:border-white/10 flex items-center justify-between">
                  <span className="text-sm font-semibold">Round {currentRoundNo}{roundCount ? ` / ${roundCount}` : ''}</span>
                  <span className="text-xs opacity-40">{amrapRoundTimes.length} done</span>
                </div>
                {roundExercises.length === 0 ? <div className="px-4 py-4 text-sm opacity-50">{t('No exercises', 'No exercises')}</div> : (
                  <div className="divide-y divide-gray-200/60 dark:divide-white/10">
                    {roundExercises.map((ex) => (
                      <div key={`${ex.globalIdx}-${ex.name}`} className="px-4 py-3 flex items-center justify-between gap-3">
                        <div className="text-sm font-medium">{ex.name}</div>
                        {ex.detail && <div className="text-xs opacity-50 shrink-0">{ex.detail}</div>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="px-4 py-3 border-t border-gray-200/60 dark:border-white/10 flex items-center justify-between">
                  <span className="text-xs opacity-40">{roundCount ? `${roundCount - amrapRoundTimes.length} left` : ''}</span>
                  <Button variant="primary" size="sm" onClick={finishRound} disabled={actionDisabled}>
                    {`Finish Round ${currentRoundNo}${isLastRound ? ' (last)' : ''}`}
                  </Button>
                </div>
              </div>
              {roundTimesList}
              <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 px-4 py-3 flex items-center gap-3">
                <ChevronRight size={15} className="opacity-30 shrink-0" />
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-wide opacity-40 mb-0.5">{t('Next', 'Next')}</div>
                  <div className="text-sm font-medium">{!isLastRound ? `Round ${currentRoundNo + 1}` : hasNextMethod ? `Method ${blockIdx + 2}` : 'Finish workout'}</div>
                </div>
              </div>
            </>)}

            {/* ── Complex: the whole block, once per interval ── */}
            {/* The timer card is already rendered above for every non-default
                view, and it carries the "Next up in" interval clock — don't
                render a second one here. */}
            {!isTransitionPending && isComplexEmom && (<>
              <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50/60 dark:bg-white/5 border-b border-gray-200/60 dark:border-white/10 flex items-center justify-between">
                  <span className="text-sm font-semibold">{t('Round', 'Round')} {currentRoundNo}{roundCount ? ` / ${roundCount}` : ''}</span>
                  <span className="text-xs opacity-40">{t('Complex', 'Complex')}</span>
                </div>
                {roundExercises.length === 0 ? <div className="px-4 py-4 text-sm opacity-50">{t('No exercises', 'No exercises')}</div> : (
                  <div className="divide-y divide-gray-200/60 dark:divide-white/10">
                    {roundExercises.map((ex) => (
                      <div key={`${ex.globalIdx}-${ex.name}`} className="px-4 py-3 flex items-center justify-between gap-3">
                        <div className="text-sm font-medium">{ex.name}</div>
                        {ex.detail && <div className="text-xs opacity-50 shrink-0">{ex.detail}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* No "finish round" here — the interval clock advances the round,
                  and whatever is left of it is the rest. */}
              <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 px-4 py-3 flex items-center gap-3">
                <ChevronRight size={15} className="opacity-30 shrink-0" />
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-wide opacity-40 mb-0.5">{t('Next', 'Next')}</div>
                  <div className="text-sm font-medium">{!isLastRound ? `${t('Round', 'Round')} ${currentRoundNo + 1}` : hasNextMethod ? `Method ${blockIdx + 2}` : 'Finish workout'}</div>
                </div>
              </div>
            </>)}

            {/* ── Default set-by-set ── */}
            {isDefaultView && (<>

              {/* Timer — full width, upper */}
              {renderTimerCard()}

              {/* Next up — next 2 sets, centred in the middle space */}
              <div className="mt-auto rounded-2xl border border-gray-200/60 dark:border-white/10 px-4 py-3.5">
                <div className="text-[10px] font-semibold uppercase tracking-widest opacity-40 mb-2.5">{t('Next up', 'Next up')}</div>
                {upcomingSteps.length === 0 ? (
                  <div className="text-sm opacity-40">{t('Last set', 'Last set')}</div>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    {upcomingSteps.map((s, i) => (
                      <div key={i} className="text-sm leading-snug">
                        <span className="font-semibold">{s.name}</span>
                        {s.detail && <span className="opacity-50">{` ${s.detail}`}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Active set — anchored to the bottom */}
              <div className="flex flex-col gap-2 mt-auto">
                {Array.from({ length: displayTargetSets }).map((_, sIdx) => {
                  const isActive = sIdx === (setNo - 1);
                  const rec = currentGlobalIdx != null ? progress[currentGlobalIdx]?.sets?.[sIdx] : null;
                  if (!isActive) return null;

                  const recordedLine = (typeof rec?.timeMs === 'number' || typeof rec?.weight === 'number')
                    ? `Saved: ${typeof rec?.timeMs === 'number' ? fmtTime(rec!.timeMs) : '—'}${typeof rec?.weight === 'number' ? ` · ${rec!.weight} ${secondaryMetricUnit}` : ''}`
                    : null;

                  return (
                    <div key={sIdx} className={['rounded-2xl border-2 overflow-hidden transition-colors', setDoneConfirmed ? 'border-emerald-400/60 dark:border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-900/10' : 'border-brand-400/50 dark:border-brand-500/40 bg-brand-50/40 dark:bg-brand-900/10'].join(' ')}>

                      {/* Active set header */}
                      <div className={['px-4 pt-3 pb-2.5 flex items-center justify-between border-b', setDoneConfirmed ? 'border-emerald-200/60 dark:border-emerald-800/40' : 'border-brand-200/50 dark:border-brand-800/40'].join(' ')}>
                        <div className="flex items-center gap-2">
                          {setDoneConfirmed && <Check size={15} className="text-emerald-600 dark:text-emerald-400 shrink-0" />}
                          <span className={['text-sm font-bold', setDoneConfirmed ? 'text-emerald-700 dark:text-emerald-300' : 'text-brand-700 dark:text-brand-300'].join(' ')}>
                            {roundLabel} {sIdx + 1} <span className="opacity-50 font-normal">/ {displayTargetSets}</span>
                          </span>
                          {setDoneConfirmed && <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 opacity-70">{t('Done', 'Done')}</span>}
                        </div>
                        {!setDoneConfirmed && (
                          <button type="button" className="flex items-center gap-1 text-[11px] font-medium text-brand-600 dark:text-brand-400 hover:opacity-70 transition disabled:opacity-30" onClick={handleResetExerciseTimer} disabled={actionDisabled}>
                            <RotateCcw size={13} /> {t('Re-time', 'Re-time')}
                          </button>
                        )}
                      </div>

                      {setDoneConfirmed ? (
                        /* ── Set complete: summary + Next button ── */
                        <div className={`px-4 py-4 flex flex-col gap-4 ${setBodyMinHClass}`}>
                          {/* Current exercise */}
                          <div className="text-base font-bold leading-tight text-center truncate">
                            {current ? (current.nameRaw ?? (current as any)?.raw ?? `Exercise ${currentBlockPos != null ? currentBlockPos + 1 : 1}`) : t('No exercise', 'No exercise')}
                          </div>
                          {/* Stats summary */}
                          <div className="grid grid-cols-3 gap-2">
                            <div className="rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 bg-white/60 dark:bg-gray-900/40 px-2 py-2.5 text-center">
                              <div className="text-[9px] font-semibold uppercase tracking-widest opacity-40 mb-1">{repLabel}</div>
                              <div className="text-3xl font-black tabular-nums">{repInput !== '' ? repInput : '–'}</div>
                              <div className="text-[10px] opacity-40 mt-0.5">{currentRepUnit}</div>
                            </div>
                            <div className="rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 bg-white/60 dark:bg-gray-900/40 px-2 py-2.5 text-center">
                              <div className="text-[9px] font-semibold uppercase tracking-widest opacity-40 mb-1">{secondaryMetricLabel}</div>
                              <div className="text-3xl font-black tabular-nums">{typeof weightInput === 'number' && weightInput > 0 ? weightInput : '–'}</div>
                              <div className="text-[10px] opacity-40 mt-0.5">{secondaryMetricUnit}</div>
                            </div>
                            <div className="rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 bg-white/60 dark:bg-gray-900/40 px-2 py-2.5 text-center">
                              <div className="text-[9px] font-semibold uppercase tracking-widest opacity-40 mb-1">Time</div>
                              <div className="text-xl font-black tabular-nums leading-tight mt-1">{fmtTime(exerciseMs)}</div>
                            </div>
                          </div>

                          {!isCurrentShuttleSprintExercise && currentPlateStack && (
                            <div className="flex justify-center">
                              <BarbellStack totalKg={currentWeightValue!} strategy={plateStrategy} rxLoadLabel={currentWodRxLoad} />
                            </div>
                          )}

                          {/* Next button */}
                          <Button variant="success" onClick={() => saveAndNext({ skipSave: true })} disabled={actionDisabled} className="w-full justify-center mt-auto">
                            <Check size={15} />
                            {isLastStepInBlock
                              ? (hasNextMethod ? t('Next Method', 'Next Method') : t('Finish Workout', 'Finish Workout'))
                              : isNextSameExercise
                                ? `${t('Next', 'Next')} ${roundLabel}`
                                : t('Next Exercise', 'Next Exercise')}
                          </Button>
                        </div>
                      ) : (
                        /* ── Active: inputs + Set Done button ── */
                        <div className={`px-4 py-4 flex flex-col gap-4 ${setBodyMinHClass}`}>
                          {/* Current exercise */}
                          <div className="text-base font-bold leading-tight text-center truncate">
                            {current ? (current.nameRaw ?? (current as any)?.raw ?? `Exercise ${currentBlockPos != null ? currentBlockPos + 1 : 1}`) : t('No exercise', 'No exercise')}
                          </div>
                          {/* Reps stepper */}
                          <div className="flex flex-col items-center gap-2">
                            <div className="text-[10px] font-semibold uppercase tracking-widest opacity-40">{repLabel}</div>
                            <div className="flex items-center gap-4">
                              <button type="button" className="h-12 w-12 rounded-full border-2 border-brand-300 dark:border-brand-700 text-2xl font-bold text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/30 active:scale-95 transition disabled:opacity-30" onClick={() => adjustRep(-1)} disabled={controlsDisabled || currentGlobalIdx == null} aria-label="Decrease reps">−</button>
                              <input type="number" inputMode="numeric" min={1} className={`w-40 text-center ${repFontClass} font-black tabular-nums bg-transparent outline-none appearance-none leading-none`} value={repInput} onChange={(e) => handleRepInputChange(e.target.value)} placeholder={repPlaceholder} aria-label={`${repLabel} for current ${roundLabel.toLowerCase()}`} disabled={controlsDisabled || currentGlobalIdx == null} />
                              <button type="button" className="h-12 w-12 rounded-full border-2 border-brand-300 dark:border-brand-700 text-2xl font-bold text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/30 active:scale-95 transition disabled:opacity-30" onClick={() => adjustRep(1)} disabled={controlsDisabled || currentGlobalIdx == null} aria-label="Increase reps">+</button>
                            </div>
                          </div>

                          {/* Weight + Set time */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-[10px] font-semibold uppercase tracking-widest opacity-40 mb-1.5">{secondaryMetricLabel}</div>
                              <input type="number" inputMode={isCurrentShuttleSprintExercise ? 'numeric' : 'decimal'} step={isCurrentShuttleSprintExercise ? 1 : 0.5} min={isCurrentShuttleSprintExercise ? 1 : 0} className="input w-full text-center text-2xl font-bold tabular-nums !py-2" value={weightInput} onChange={(e) => { const v = e.target.value; setWeightInput(v === '' ? '' : Number(v)); }} placeholder="0" />
                              {!isCurrentShuttleSprintExercise && currentPlateStack && (
                                <div className="mt-2 flex justify-center">
                                  <BarbellStack totalKg={currentWeightValue!} strategy={plateStrategy} rxLoadLabel={currentWodRxLoad} />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="text-[10px] font-semibold uppercase tracking-widest opacity-40 mb-1.5">{roundLabel} Time</div>
                              <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 flex items-center justify-center py-2 text-2xl font-bold tabular-nums">{fmtTime(exerciseMs)}</div>
                              {recordedLine && <div className="text-[11px] opacity-40 mt-1 text-center truncate">{recordedLine}</div>}
                            </div>
                          </div>

                          {/* Actions */}
                          {isEmom ? (
                            <Button variant="primary" onClick={() => void saveAndNext()} disabled={actionDisabled} className="w-full justify-center mt-auto">
                              <Check size={15} /> {t('Save Set', 'Save Set')}
                            </Button>
                          ) : (
                            <Button variant="primary" onClick={handleSetDone} disabled={actionDisabled} className="w-full justify-center mt-auto">
                              <Check size={15} /> {t('Set Done', 'Set Done')}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>)}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-4 py-3 border-t border-gray-200/60 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.03] flex items-center justify-between gap-3 shrink-0">
          {(amrapTargetMs || emomTargetMs) && !isTransitionPending ? (
            <Button variant="secondary" onClick={() => { if (hasNextMethod) { queueNextMethod(blockIdx + 1); } else { void finishWorkout(); } }} disabled={actionDisabled}>
              {isEmom ? t('Finish EMOM', 'Finish EMOM') : t('Finish AMRAP', 'Finish AMRAP')}
            </Button>
          ) : (
            /* The whole workout's pause, in the slot AMRAP/EMOM use for their
               Finish button — so it appears for the set- and round-based methods,
               which are the ones that count pauses set by set. */
            <div className={['flex flex-col items-start', paused ? 'text-amber-600 dark:text-amber-300' : 'opacity-40'].join(' ')}>
              <span className="text-[10px] font-semibold uppercase tracking-widest leading-none mb-0.5">{t('Total pause', 'Total pause')}</span>
              <span className="text-base font-bold tabular-nums leading-none">{fmtTime(totalPauseMs)}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className={['flex flex-col items-end', paused ? 'text-amber-600 dark:text-amber-300' : restCountdownActive ? (restFinalSeconds ? 'text-rose-500' : 'opacity-60') : 'opacity-40'].join(' ')}>
              <span className="text-[10px] font-semibold uppercase tracking-widest leading-none mb-0.5">{pauseLabel}</span>
              <span className="text-base font-bold tabular-nums leading-none">{fmtTime(pauseDisplayMs)}</span>
            </div>
            {!paused ? (
              <Button variant="primary" onClick={handlePause} disabled={pauseDisabled} className="px-6">
                <Pause size={16} /> Pause
              </Button>
            ) : (
              <Button variant="success" onClick={handleResume} disabled={pauseDisabled} className="px-6">
                <Play size={16} /> Resume
              </Button>
            )}
          </div>
        </div>
      </div>
      </ErrorBoundary>
    </div>,
    document.body
  );
}
